if (!customElements.get('vo-product-accordion')) {
  customElements.define(
    'vo-product-accordion',
    class VoProductAccordion extends HTMLElement {
      connectedCallback() {
        this.accordionAnimations = this.accordionAnimations || new WeakMap();
        this.onClickHandler = (event) => this.onSummaryClick(event);
        this.onToggleHandler = (event) => this.onToggle(event);
        this.disableNativeDetailsGrouping();
        this.addEventListener('click', this.onClickHandler);
        this.addEventListener('toggle', this.onToggleHandler, true);
        this.querySelectorAll('details[open][data-vo-product-accordion-item]').forEach((details) => {
          details.dataset.voAccordionState = 'open';
        });
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onClickHandler);
        this.removeEventListener('toggle', this.onToggleHandler, true);
      }

      disableNativeDetailsGrouping() {
        this.querySelectorAll('details[name][data-vo-product-accordion-item]').forEach((details) => {
          if (!details.dataset.voAccordionName) {
            details.dataset.voAccordionName = details.getAttribute('name') || '';
          }
          details.removeAttribute('name');
        });
      }

      getDetailsFromSummary(summary) {
        const details = summary?.parentElement;
        if (
          !(details instanceof HTMLElement) ||
          !details.matches('details[data-vo-product-accordion-item]') ||
          !this.contains(details)
        ) {
          return null;
        }

        return details;
      }

      getContent(details) {
        return details.querySelector('[data-vo-product-accordion-content]');
      }

      onSummaryClick(event) {
        const summary = event.target instanceof Element ? event.target.closest('summary') : null;
        const details = this.getDetailsFromSummary(summary);
        if (!details) return;

        event.preventDefault();

        if (details.open && details.dataset.voAccordionState !== 'closing') {
          this.closeDetails(details);
          return;
        }

        this.openDetails(details);
      }

      onToggle(event) {
        const activeDetails = event.target;

        if (
          !(activeDetails instanceof HTMLElement) ||
          !activeDetails.matches('details[data-vo-product-accordion-item]') ||
          !activeDetails.open ||
          !this.contains(activeDetails)
        ) {
          return;
        }

        activeDetails.dataset.voAccordionState = activeDetails.dataset.voAccordionState || 'open';
        this.closeOtherDetails(activeDetails);
      }

      closeOtherDetails(activeDetails) {
        this.querySelectorAll('details[open][data-vo-product-accordion-item]').forEach((details) => {
          if (details !== activeDetails) this.closeDetails(details);
        });
      }

      openDetails(details) {
        const content = this.getContent(details);
        this.cancelAnimation(content);
        this.closeOtherDetails(details);

        if (!content || this.prefersReducedMotion()) {
          details.open = true;
          details.dataset.voAccordionState = 'open';
          this.resetContentStyles(content);
          return;
        }

        const targetPaddingBottom = this.getContentPaddingBottom(content);
        details.open = true;
        details.dataset.voAccordionState = 'opening';
        content.style.height = '0px';
        content.style.opacity = '0';
        content.style.overflow = 'hidden';
        content.style.paddingBottom = '0px';

        window.requestAnimationFrame(() => {
          if (details.dataset.voAccordionState !== 'opening') return;
          content.style.paddingBottom = targetPaddingBottom;
          content.style.height = `${content.scrollHeight}px`;
          content.style.opacity = '1';
        });

        this.trackAnimation(content, () => {
          if (details.dataset.voAccordionState !== 'opening') return;
          details.dataset.voAccordionState = 'open';
          this.resetContentStyles(content);
        });
      }

      closeDetails(details) {
        const content = this.getContent(details);
        this.cancelAnimation(content);

        if (!details.open || !content || this.prefersReducedMotion()) {
          details.open = false;
          details.dataset.voAccordionState = 'closed';
          this.resetContentStyles(content);
          return;
        }

        const currentPaddingBottom = this.getContentPaddingBottom(content);
        details.dataset.voAccordionState = 'closing';
        content.style.height = `${content.scrollHeight}px`;
        content.style.opacity = '1';
        content.style.overflow = 'hidden';
        content.style.paddingBottom = currentPaddingBottom;
        content.getBoundingClientRect();

        window.requestAnimationFrame(() => {
          if (details.dataset.voAccordionState !== 'closing') return;
          content.style.height = '0px';
          content.style.opacity = '0';
          content.style.paddingBottom = '0px';
        });

        this.trackAnimation(content, () => {
          if (details.dataset.voAccordionState !== 'closing') return;
          details.open = false;
          details.dataset.voAccordionState = 'closed';
          this.resetContentStyles(content);
        });
      }

      getContentPaddingBottom(content) {
        return window.getComputedStyle(content).paddingBottom || '0px';
      }

      prefersReducedMotion() {
        return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      }

      resetContentStyles(content) {
        if (!content) return;

        content.style.height = '';
        content.style.opacity = '';
        content.style.overflow = '';
        content.style.paddingBottom = '';
      }

      cancelAnimation(content) {
        const cleanup = content ? this.accordionAnimations?.get(content) : null;
        if (cleanup) cleanup();
      }

      trackAnimation(content, onComplete) {
        const complete = () => {
          content.removeEventListener('transitionend', onTransitionEnd);
          window.clearTimeout(fallbackTimer);
          this.accordionAnimations.delete(content);
          onComplete();
        };
        const onTransitionEnd = (event) => {
          if (event.target === content && event.propertyName === 'height') complete();
        };
        const fallbackTimer = window.setTimeout(complete, 380);

        this.accordionAnimations.set(content, () => {
          content.removeEventListener('transitionend', onTransitionEnd);
          window.clearTimeout(fallbackTimer);
          this.accordionAnimations.delete(content);
        });
        content.addEventListener('transitionend', onTransitionEnd);
      }
    }
  );
}

if (!customElements.get('vo-product-variant-picker')) {
  customElements.define(
    'vo-product-variant-picker',
    class VoProductVariantPicker extends HTMLElement {
      connectedCallback() {
        this.onClickHandler = (event) => this.onClick(event);
        this.onMouseOverHandler = (event) => this.onMouseOver(event);
        this.onMouseLeaveHandler = () => this.syncMetaToSelected();
        this.onFocusInHandler = (event) => this.onFocusIn(event);
        this.onFocusOutHandler = (event) => this.onFocusOut(event);

        this.addEventListener('click', this.onClickHandler);
        this.addEventListener('mouseover', this.onMouseOverHandler);
        this.addEventListener('mouseleave', this.onMouseLeaveHandler);
        this.addEventListener('focusin', this.onFocusInHandler);
        this.addEventListener('focusout', this.onFocusOutHandler);

        this.syncMetaToSelected();
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onClickHandler);
        this.removeEventListener('mouseover', this.onMouseOverHandler);
        this.removeEventListener('mouseleave', this.onMouseLeaveHandler);
        this.removeEventListener('focusin', this.onFocusInHandler);
        this.removeEventListener('focusout', this.onFocusOutHandler);
      }

      onMouseOver(event) {
        const variantButton = event.target.closest('[data-variant-button]');
        if (!variantButton || !this.contains(variantButton)) return;

        this.updateMeta(variantButton);
      }

      onFocusIn(event) {
        const variantButton = event.target.closest('[data-variant-button]');
        if (!variantButton || !this.contains(variantButton)) return;

        this.updateMeta(variantButton);
      }

      onFocusOut(event) {
        if (this.contains(event.relatedTarget)) return;
        this.syncMetaToSelected();
      }

      onClick(event) {
        const variantButton = event.target.closest('[data-variant-button]');
        if (!variantButton || !this.contains(variantButton)) return;
        const partialOptionValues = getPartialOptionValuesFromDataset(variantButton.dataset);

        const productUrl = variantButton.dataset.voProductUrl;
        if (productUrl) {
          event.preventDefault();
          if (!variantButton.classList.contains('is-selected')) {
            navigateToLinkedProduct(getSectionIdFromElement(variantButton), productUrl, partialOptionValues);
          }
          return;
        }

        this.setSelectedButton(variantButton);

        const variantSelects = this.closest('variant-selects');
        const sectionId = variantSelects?.dataset.section;
        if (!sectionId) return;

        const matrix = getVariantMatrix(sectionId);
        const currentOptionValues = getCurrentOptionValues(sectionId);
        const nextVariant = findVariantByPartialOptions(matrix, currentOptionValues, partialOptionValues);

        if (nextVariant) {
          applyVariantOptionValues(sectionId, nextVariant.options);
          return;
        }

        const optionValues = [...currentOptionValues];
        Object.entries(partialOptionValues).forEach(([index, value]) => {
          optionValues[Number(index)] = value;
        });
        applyVariantOptionValues(sectionId, optionValues);
      }

      setSelectedButton(variantButton) {
        this.querySelectorAll('[data-variant-button]').forEach((button) => {
          const isSelected = button === variantButton;
          button.classList.toggle('is-selected', isSelected);
          button.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
        });

        this.updateMeta(variantButton);
      }

      syncMetaToSelected() {
        const selectedButton =
          this.querySelector('[data-variant-button][aria-pressed="true"]') || this.querySelector('[data-variant-button]');

        if (selectedButton) this.updateMeta(selectedButton);
      }

      updateMeta(variantButton) {
        const option1Target = this.querySelector('[data-variant-meta-value="option1"]');
        const option2Target = this.querySelector('[data-variant-meta-value="option2"]');
        const option1Line = this.querySelector('[data-variant-meta-line="option1"]');
        const option2Line = this.querySelector('[data-variant-meta-line="option2"]');
        const previewVariant = getPreviewVariantForControl(variantButton);
        const option1DisplayValue =
          previewVariant?.frame || variantButton.dataset.displayOption1 || variantButton.dataset.option1 || '';
        const option2DisplayValue =
          previewVariant?.lenses || variantButton.dataset.displayOption2 || variantButton.dataset.option2 || '';

        if (option1Target) option1Target.textContent = option1DisplayValue;
        if (option2Target) option2Target.textContent = option2DisplayValue;
        if (option1Line) option1Line.hidden = !option1DisplayValue;
        if (option2Line) option2Line.hidden = !option2DisplayValue;

        const sectionId = getSectionIdFromElement(variantButton);
        if (sectionId && previewVariant) {
          applyVariantPreview(sectionId, previewVariant);
        }
      }
    }
  );
}

if (!customElements.get('vo-measurements-toggler')) {
  customElements.define(
    'vo-measurements-toggler',
    class VoMeasurementsToggler extends HTMLElement {
      constructor() {
        super();
        this.mmToInchFactor = 1 / 25.4;
      }

      connectedCallback() {
        this.measurementToggleInput = this.querySelector('[data-measurement-toggle]');
        this.measurementToggleLabels = Array.from(this.querySelectorAll('[data-measurement-toggle-label]'));

        this.onToggleChangeHandler = (event) => this.handleMeasurementToggle(event);
        this.onToggleLabelClickHandler = (event) => this.handleToggleLabelClick(event);

        this.measurementToggleInput?.addEventListener('change', this.onToggleChangeHandler);
        this.addEventListener('click', this.onToggleLabelClickHandler);

        this.setMeasureUnit(this.dataset.defaultUnit || this.dataset.unit || 'mm', { dispatchEvent: false });
      }

      disconnectedCallback() {
        this.measurementToggleInput?.removeEventListener('change', this.onToggleChangeHandler);
        this.removeEventListener('click', this.onToggleLabelClickHandler);
      }

      handleMeasurementToggle(event) {
        const measureUnit = event.target.checked ? 'inch' : 'mm';
        this.setMeasureUnit(measureUnit);
      }

      handleToggleLabelClick(event) {
        const measureUnitLabel = event.target.closest('[data-measurement-toggle-label]');
        if (!(measureUnitLabel instanceof HTMLElement) || !this.contains(measureUnitLabel)) return;

        event.preventDefault();
        this.setMeasureUnit(measureUnitLabel.dataset.unit || 'mm');
      }

      setMeasureUnit(measureUnit, { dispatchEvent = true } = {}) {
        const normalizedUnit = measureUnit === 'inch' ? 'inch' : 'mm';
        this.dataset.unit = normalizedUnit;

        if (this.measurementToggleInput) {
          this.measurementToggleInput.checked = normalizedUnit === 'inch';
        }

        this.measurementToggleLabels.forEach((label) => {
          const isActive = label.dataset.unit === normalizedUnit;
          label.classList.toggle('is-active', isActive);
          label.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        });

        this.updateMeasurements(normalizedUnit);

        if (dispatchEvent) {
          window.dispatchEvent(
            new CustomEvent('vo:measurement-toggle-changed', {
              detail: { measureUnit: normalizedUnit },
              bubbles: true,
            })
          );
        }
      }

      updateMeasurements(measureUnit) {
        this.getMeasurementElements().forEach((measurementElement) => {
          const mmValue = toNumber(measurementElement.dataset.mmValue);
          if (mmValue === null) return;

          measurementElement.textContent = formatMeasurementValue(mmValue, measureUnit);
        });
      }

      getMeasurementElements() {
        return Array.from(this.closest('dialog[data-vo-overlay]')?.querySelectorAll('[data-vo-guide-measurement]') || []);
      }
    }
  );
}

if (!customElements.get('vo-sticky-cta')) {
  customElements.define(
    'vo-sticky-cta',
    class VoStickyCta extends HTMLElement {
      connectedCallback() {
        this.onStickyClickHandler = (event) => this.onStickyClick(event);
        this.onViewportChangeHandler = () => this.refreshVisibility();
        this.sectionId = this.dataset.sectionId;
        this.isFooterInViewport = false;

        this.selfMutationObserver = new MutationObserver(() => this.refreshBindings());
        this.selfMutationObserver.observe(this, { childList: true });

        if (typeof subscribe === 'function') {
          this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            if (event?.data?.sectionId !== this.sectionId) return;
            this.refreshBindings();
          });
        }

        this.refreshBindings();
      }

      disconnectedCallback() {
        this.selfMutationObserver?.disconnect();
        this.variantChangeUnsubscriber?.();
        this.teardownStickyButton();
        this.teardownSourceObservers();
        this.teardownFooterObserver();
        window.removeEventListener('scroll', this.onViewportChangeHandler);
        window.removeEventListener('resize', this.onViewportChangeHandler);
      }

      refreshBindings() {
        this.refreshStickyButton();
        this.refreshSourceButton();
        this.refreshFooterBoundary();
        this.syncState();
        this.refreshVisibility();
      }

      refreshStickyButton() {
        const nextStickyButton = this.querySelector('[data-vo-sticky-cta-button]');
        if (this.stickyButton === nextStickyButton) return;

        this.teardownStickyButton();
        this.stickyButton = nextStickyButton;
        this.stickyButton?.addEventListener('click', this.onStickyClickHandler);
      }

      teardownStickyButton() {
        this.stickyButton?.removeEventListener('click', this.onStickyClickHandler);
      }

      refreshSourceButton() {
        const nextSourceButton = this.getSourceButton();
        if (this.sourceButton === nextSourceButton) return;

        this.teardownSourceObservers();
        this.sourceButton = nextSourceButton;

        if (!this.sourceButton) return;

        this.sourceMutationObserver = new MutationObserver(() => this.syncState());
        this.sourceMutationObserver.observe(this.sourceButton, {
          attributes: true,
          attributeFilter: ['aria-disabled', 'class', 'data-vo-cta-state', 'disabled', 'hidden'],
          characterData: true,
          childList: true,
          subtree: true,
        });

        if ('IntersectionObserver' in window) {
          this.visibilityObserver = new IntersectionObserver((entries) => {
            this.isSourceButtonInViewport = entries.some((entry) => entry.isIntersecting);
            this.updateVisibility();
          });
          this.visibilityObserver.observe(this.sourceButton);
        } else {
          window.addEventListener('scroll', this.onViewportChangeHandler, { passive: true });
          window.addEventListener('resize', this.onViewportChangeHandler, { passive: true });
        }
      }

      refreshFooterBoundary() {
        const nextFooterBoundary = document.querySelector('[data-vo-footer-boundary], footer');
        const nextRootMargin = this.getFooterVisibilityRootMargin();

        if (this.footerBoundary === nextFooterBoundary && this.footerVisibilityRootMargin === nextRootMargin) {
          this.refreshFooterVisibility();
          return;
        }

        this.teardownFooterObserver();
        this.footerBoundary = nextFooterBoundary;
        this.footerVisibilityRootMargin = nextRootMargin;
        this.isFooterInViewport = false;

        if (!this.footerBoundary) return;

        if ('IntersectionObserver' in window) {
          this.footerVisibilityObserver = new IntersectionObserver(
            (entries) => {
              this.isFooterInViewport = entries.some((entry) => entry.isIntersecting);
              this.updateVisibility();
            },
            { rootMargin: this.footerVisibilityRootMargin }
          );
          this.footerVisibilityObserver.observe(this.footerBoundary);
        } else {
          this.refreshFooterVisibility();
          window.addEventListener('scroll', this.onViewportChangeHandler, { passive: true });
          window.addEventListener('resize', this.onViewportChangeHandler, { passive: true });
        }
      }

      teardownFooterObserver() {
        this.footerVisibilityObserver?.disconnect();
        this.footerVisibilityObserver = null;
        this.footerBoundary = null;
      }

      getFooterVisibilityRootMargin() {
        const stickyHeight = Math.max(this.getStickyHeight(), 96);

        return `0px 0px ${stickyHeight}px 0px`;
      }

      getStickyHeight() {
        const stickyRect = this.getBoundingClientRect();
        const innerRect = this.querySelector('.vo-sticky-cta__inner')?.getBoundingClientRect();

        return Math.ceil(stickyRect.height || innerRect?.height || 0);
      }

      refreshFooterVisibility() {
        if (!this.footerBoundary) {
          this.isFooterInViewport = false;
          return;
        }

        const footerRect = this.footerBoundary.getBoundingClientRect();
        const stickyHeight = Math.max(this.getStickyHeight(), 96);
        this.isFooterInViewport = footerRect.top < window.innerHeight + stickyHeight && footerRect.bottom > 0;
      }

      teardownSourceObservers() {
        this.sourceMutationObserver?.disconnect();
        this.sourceMutationObserver = null;
        this.visibilityObserver?.disconnect();
        this.visibilityObserver = null;
        window.removeEventListener('scroll', this.onViewportChangeHandler);
        window.removeEventListener('resize', this.onViewportChangeHandler);
        this.sourceButton = null;
      }

      getSourceButton() {
        if (!this.sectionId) return null;

        return (
          document.getElementById(`ProductSubmitButton-${this.sectionId}`) ||
          document.querySelector(`[data-vo-buy-box][data-section-id="${this.sectionId}"] [data-vo-cta-button]`)
        );
      }

      onStickyClick(event) {
        this.refreshSourceButton();

        if (!this.sourceButton || this.stickyButton?.disabled || this.stickyButton?.getAttribute('aria-disabled') === 'true') {
          event.preventDefault();
          return;
        }

        event.preventDefault();
        this.sourceButton.click();
      }

      syncState() {
        if (!this.sourceButton || !this.stickyButton) return;

        const sourceLabel = this.sourceButton.querySelector('span');
        const stickyLabel = this.stickyButton.querySelector('[data-vo-sticky-cta-label]');
        const isDisabled = this.sourceButton.hasAttribute('disabled');
        const ariaDisabled = this.sourceButton.getAttribute('aria-disabled');
        const isLoading = this.sourceButton.classList.contains('loading');
        const stickySpinner = this.stickyButton.querySelector('.loading__spinner');

        if (stickyLabel && sourceLabel) {
          stickyLabel.textContent = sourceLabel.textContent.trim();
        }

        this.syncDataAttribute('voCtaState');
        this.syncDataAttribute('voNotifyModal');
        this.syncDataAttribute('voAlternativesUrl');
        this.syncAttribute('data-element-id');
        this.syncAttribute('data-description');
        this.syncAttribute('data-tracking-type');
        this.syncAttribute('aria-controls');
        this.syncAttribute('aria-haspopup');

        this.stickyButton.toggleAttribute('disabled', isDisabled);

        if (ariaDisabled != null) {
          this.stickyButton.setAttribute('aria-disabled', ariaDisabled);
        } else {
          this.stickyButton.removeAttribute('aria-disabled');
        }

        this.stickyButton.classList.toggle('loading', isLoading);
        if (stickySpinner) stickySpinner.classList.toggle('hidden', !isLoading);

        this.updateVisibility();
      }

      syncDataAttribute(name) {
        if (!this.sourceButton || !this.stickyButton) return;

        const value = this.sourceButton.dataset[name];
        if (value) {
          this.stickyButton.dataset[name] = value;
        } else {
          delete this.stickyButton.dataset[name];
        }
      }

      syncAttribute(name) {
        if (!this.sourceButton || !this.stickyButton) return;

        const value = this.sourceButton.getAttribute(name);
        if (value != null && value !== '') {
          this.stickyButton.setAttribute(name, value);
        } else {
          this.stickyButton.removeAttribute(name);
        }
      }

      refreshVisibility() {
        this.refreshFooterVisibility();

        if (!this.sourceButton) {
          this.updateVisibility();
          return;
        }

        if (!this.visibilityObserver) {
          const sourceRect = this.sourceButton.getBoundingClientRect();
          this.isSourceButtonInViewport = sourceRect.bottom > 0 && sourceRect.top < window.innerHeight;
        }

        this.updateVisibility();
      }

      updateVisibility() {
        if (!this.sourceButton || !this.stickyButton) {
          this.classList.remove('is-visible');
          return;
        }

        const isSourceHidden =
          this.sourceButton.hidden ||
          this.sourceButton.closest('[hidden]') ||
          this.sourceButton.getAttribute('aria-hidden') === 'true';
        const shouldShow = !this.isSourceButtonInViewport && !isSourceHidden;

        this.classList.toggle('is-visible', shouldShow && !this.isFooterInViewport);
      }
    }
  );
}

const VO_CTA_STATES = {
  addToBag: 'add-to-bag',
  notifyMe: 'notify-me',
  soldOut: 'sold-out',
  viewAlternatives: 'view-alternatives',
};

const VO_NOTIFY_APP_KEYWORDS = [
  'notify',
  'notify me',
  'avvisami',
  'avvisa',
  'notifica',
  'back in stock',
  'back-in-stock',
  'back_in_stock',
  'bis',
  'restock',
  'stock alert',
  'email me',
  'email when available',
  'when available',
  'available again',
  'out of stock',
  'esaurito',
  'non disponibile',
  'torna disponibile',
  'disponibile di nuovo',
  'appikon',
  'globo',
  'klaviyo',
];

const VO_NOTIFY_APP_SCAN_SELECTOR = [
  'button',
  'a',
  'input',
  'form',
  'iframe',
  '[role="button"]',
  '[id]',
  '[class]',
  '[data-testid]',
  '[data-app]',
].join(',');

const VO_NOTIFY_APP_LOCATION_SELECTOR = [
  '.vo-product-form > form[data-type="add-to-cart-form"] ~ *',
].join(',');

const VO_NOTIFY_RESTOCK_WRAPPER_SELECTOR = '.restock-alerts-modal-wrapper';
const VO_NOTIFY_RESTOCK_CONTACT_FIELD_SELECTOR = [
  'input.restock-alerts-email-input',
  'input.email-input',
  'input[class*="email-input" i]',
  'input[name*="email" i]',
  'input[placeholder*="email" i]',
  'input[placeholder*="e-mail" i]',
  'input[placeholder*="mail" i]',
  'input[class*="sms-input" i]',
  'input[class*="phone" i]',
  'input[name*="phone" i]',
  'input[placeholder*="phone" i]',
  'input[placeholder*="telefono" i]',
  'input[placeholder*="numero" i]',
].join(',');

const VO_NOTIFY_RESTOCK_SUBMIT_SELECTOR = [
  '.restock-alerts-submit-button',
  '.notifyButtonStyle',
  'button[type="submit"]',
  'input[type="submit"]',
].join(',');

const VO_NOTIFY_RESTOCK_SUCCESS_SELECTOR = [
  '[data-vo-notify-app-success]',
  '.restock-alerts-success',
  '.restock-alerts-success-message',
  '.restock-alerts-message-success',
  '.restock-alerts-thank-you',
  '.restock-alerts-confirmation',
  '[class*="success" i]',
  '[class*="thank" i]',
].join(',');

const VO_NOTIFY_RESTOCK_SUBMIT_RESTORE_DELAY = 60;
const VO_NOTIFY_RESTOCK_SUBMIT_STORAGE_KEY = 'voNotifySubmitState';
const VO_NOTIFY_RESTOCK_SUBMIT_STORAGE_TTL = 5 * 60 * 1000;
const VO_NOTIFY_OVERLAY_AUTO_REOPEN_GUARD = 2500;

const VO_NOTIFY_APP_OWNED_SELECTOR = [
  '[data-vo-overlay]',
  '[data-vo-overlay-root]',
  '[data-vo-cta-button]',
  '[data-vo-sticky-cta-button]',
  '[data-vo-overlay-trigger]',
  '[data-vo-cart-notification]',
  '[la-dn-drawercart-root]',
  '[data-vo-notify-native-form]',
  '.vo-pdp-notify-form',
  '.vo-cart-notification',
  '#cart-notification',
  'vo-cart-notification',
  '[data-vo-product-card-control]',
  '[data-vo-smartwishlist-button]',
  'vo-vmmv',
  '.vmmv-container',
].join(',');

let notifyAppObserver = null;
let notifyAppIntegrationFrame = null;

const VO_FACE_SIZE_RANGE_FALLBACK = {
  narrow: { key: 'narrow', min: 111, max: 128 },
  average: { key: 'average', min: 123, max: 138 },
  wide: { key: 'wide', min: 133, max: 150 },
};

function getFaceSizeRange() {
  if (typeof window !== 'undefined' && window['FACE_SIZE_RANGE']) {
    return window['FACE_SIZE_RANGE'];
  }

  return VO_FACE_SIZE_RANGE_FALLBACK;
}

function getOverlayRoot(sectionId) {
  return document.getElementById(`VoPdpOverlays-${sectionId}`);
}

function getVariantSelects(sectionId) {
  return document.querySelector(`product-info[data-section="${sectionId}"] variant-selects#variant-selects-${sectionId}`);
}

function getNativeVariantSelects(sectionId) {
  const variantSelects = getVariantSelects(sectionId);
  if (!variantSelects) return [];

  return Array.from(variantSelects.querySelectorAll('.vo-product-variant-selects__native select'));
}

function getCurrentOptionValues(sectionId) {
  return getNativeVariantSelects(sectionId).map((select) => select.value);
}

function updateNativeSelectValue(select, value) {
  if (!select || value == null) return false;

  let hasChanged = false;
  if (select.value !== value) {
    select.value = value;
    hasChanged = true;
  }

  Array.from(select.options).forEach((option) => {
    option.toggleAttribute('selected', option.value === value);
  });

  return hasChanged;
}

function applyVariantOptionValues(sectionId, optionValues) {
  const nativeSelects = getNativeVariantSelects(sectionId);
  if (!nativeSelects.length) return false;

  let hasSelectionChanged = false;
  let triggerSelect = null;

  optionValues.forEach((value, index) => {
    const select = nativeSelects[index];
    if (!select || !value) return;

    hasSelectionChanged = updateNativeSelectValue(select, value) || hasSelectionChanged;
    triggerSelect = select;
  });

  if (!hasSelectionChanged || !triggerSelect) return false;
  triggerSelect.dispatchEvent(new Event('change', { bubbles: true }));
  return true;
}

function applySizeChoice(sectionId, targetValue, sizeOptionIndex = null) {
  if (!targetValue) return false;

  const nativeSelects = getNativeVariantSelects(sectionId);
  if (!nativeSelects.length) return false;

  const numericIndex =
    sizeOptionIndex !== null && sizeOptionIndex !== undefined && sizeOptionIndex !== '' ? Number(sizeOptionIndex) : null;

  let sizeSelect = null;
  if (numericIndex !== null && !Number.isNaN(numericIndex)) {
    const indexedSelect = nativeSelects[numericIndex];
    if (indexedSelect && Array.from(indexedSelect.options).some((option) => option.value === targetValue)) {
      sizeSelect = indexedSelect;
    }
  }

  if (!sizeSelect) {
    sizeSelect = nativeSelects.find((select) => Array.from(select.options).some((option) => option.value === targetValue));
  }
  if (!sizeSelect) return false;

  const optionValues = nativeSelects.map((select) => (select === sizeSelect ? targetValue : select.value));
  return applyVariantOptionValues(sectionId, optionValues);
}

function getVariantMatrix(sectionId) {
  const overlayRoot = getOverlayRoot(sectionId);
  if (!overlayRoot) return [];

  if (!overlayRoot.__voVariantMatrix) {
    const matrixScript = overlayRoot.querySelector('[data-vo-variant-matrix]');
    if (!matrixScript) return [];

    try {
      overlayRoot.__voVariantMatrix = JSON.parse(matrixScript.textContent);
    } catch (error) {
      console.error('Unable to parse Vogue PDP variant matrix.', error);
      overlayRoot.__voVariantMatrix = [];
    }
  }

  return overlayRoot.__voVariantMatrix;
}

function normalizeProductUrl(productUrl) {
  if (!productUrl) return '';

  try {
    const url = new URL(productUrl, window.location.origin);
    return url.pathname;
  } catch (error) {
    return productUrl;
  }
}

function buildProductUrlWithVariant(productUrl, variantId = null) {
  if (!productUrl) return '';

  try {
    const url = new URL(productUrl, window.location.origin);
    if (variantId) {
      url.searchParams.set('variant', variantId);
    } else {
      url.searchParams.delete('variant');
    }
    return `${url.pathname}${url.search}${url.hash}`;
  } catch (error) {
    return productUrl;
  }
}

function getLinkedProductMap(sectionId) {
  const overlayRoot = getOverlayRoot(sectionId);
  if (!overlayRoot) return {};

  if (!overlayRoot.__voLinkedProducts) {
    const linkedProductsScript = overlayRoot.querySelector('[data-vo-linked-products]');
    if (!linkedProductsScript) return {};

    try {
      const parsedLinkedProducts = JSON.parse(linkedProductsScript.textContent);
      overlayRoot.__voLinkedProducts = Object.entries(parsedLinkedProducts).reduce((linkedProducts, [productUrl, productData]) => {
        linkedProducts[productUrl] = productData;

        const normalizedProductUrl = normalizeProductUrl(productUrl);
        if (normalizedProductUrl) {
          linkedProducts[normalizedProductUrl] = productData;
        }

        return linkedProducts;
      }, {});
    } catch (error) {
      console.error('Unable to parse linked Vogue PDP products.', error);
      overlayRoot.__voLinkedProducts = {};
    }
  }

  return overlayRoot.__voLinkedProducts;
}

function getOptionValueMap(optionValues, overrideValues = {}) {
  const optionValueMap = {};

  if (Array.isArray(optionValues)) {
    optionValues.forEach((value, index) => {
      if (value != null && value !== '') {
        optionValueMap[index] = value;
      }
    });
  }

  Object.entries(overrideValues).forEach(([index, value]) => {
    if (value != null && value !== '') {
      optionValueMap[Number(index)] = value;
    }
  });

  return optionValueMap;
}

function getPartialOptionValuesFromDataset(dataset) {
  return [dataset.option1, dataset.option2, dataset.option3].reduce((partialOptionValues, value, index) => {
    if (value != null && value !== '') {
      partialOptionValues[index] = value;
    }

    return partialOptionValues;
  }, {});
}

function resolveLinkedProductVariant(sectionId, productUrl, partialOptionValues = {}) {
  const linkedProducts = getLinkedProductMap(sectionId);
  const linkedProduct = linkedProducts[normalizeProductUrl(productUrl)] || linkedProducts[productUrl];
  const targetVariants = linkedProduct?.variants;
  if (!Array.isArray(targetVariants) || !targetVariants.length) return null;

  const optionCount = targetVariants[0]?.options?.length || 0;
  const currentOptionValues = getCurrentOptionValues(sectionId).slice(0, optionCount || undefined);
  const scopedPartialOptionValues = Object.entries(partialOptionValues).reduce((filteredPartialOptionValues, [index, value]) => {
    if ((optionCount === 0 || Number(index) < optionCount) && value != null && value !== '') {
      filteredPartialOptionValues[Number(index)] = value;
    }
    return filteredPartialOptionValues;
  }, {});
  const preferredVariant = findVariantByPartialOptions(
    targetVariants,
    currentOptionValues,
    getOptionValueMap(currentOptionValues, scopedPartialOptionValues)
  );

  if (preferredVariant) return preferredVariant;

  return (
    findVariantByPartialOptions(targetVariants, currentOptionValues, scopedPartialOptionValues) ||
    targetVariants.find((variant) => variant.purchasable) ||
    targetVariants[0] ||
    null
  );
}

function navigateToLinkedProduct(sectionId, productUrl, partialOptionValues = {}) {
  if (!productUrl) return false;

  const targetVariant = sectionId ? resolveLinkedProductVariant(sectionId, productUrl, partialOptionValues) : null;
  window.location.assign(buildProductUrlWithVariant(productUrl, targetVariant?.id));
  return true;
}

function getSectionIdFromElement(element) {
  return (
    element?.dataset.sectionId ||
    element?.closest('variant-selects')?.dataset.section ||
    element?.closest('[data-vo-overlay-root]')?.dataset.sectionId ||
    null
  );
}

function getProductInfoRoot(sectionId) {
  if (!sectionId) return null;
  return document.querySelector(`product-info[data-section="${sectionId}"]`);
}

function getPreviewVariantForControl(control) {
  if (!(control instanceof HTMLElement)) return null;

  const sectionId = getSectionIdFromElement(control);
  if (!sectionId) return null;

  const partialOptionValues = getPartialOptionValuesFromDataset(control.dataset);
  const productUrl = control.dataset.voProductUrl;

  if (productUrl) {
    return resolveLinkedProductVariant(sectionId, productUrl, partialOptionValues);
  }

  const matrix = getVariantMatrix(sectionId);
  const currentOptionValues = getCurrentOptionValues(sectionId);
  return findVariantByPartialOptions(matrix, currentOptionValues, partialOptionValues);
}

function ensurePreviewElement(container, selector, className, dataAttribute, insertBefore = null) {
  if (!(container instanceof HTMLElement)) return null;

  let element = container.querySelector(selector);
  if (element) return element;

  element = document.createElement('span');
  element.className = className;
  element.setAttribute(dataAttribute, '');

  if (insertBefore) {
    container.insertBefore(element, insertBefore);
  } else {
    container.appendChild(element);
  }

  return element;
}

function setPreviewElementText(element, text) {
  if (!(element instanceof HTMLElement)) return;

  const hasText = text != null && text !== '';
  element.hidden = !hasText;

  if (hasText) {
    element.textContent = text;
  }
}

function updateGalleryPreview(productInfoRoot, previewVariant) {
  if (!(productInfoRoot instanceof HTMLElement) || !previewVariant?.previewImageSrc) return;

  const previewImage = productInfoRoot.querySelector(
    '.vo-product-gallery__track .vo-product-gallery__item:first-child .vo-product-gallery__image'
  );
  if (!(previewImage instanceof HTMLImageElement)) return;

  previewImage.src = previewVariant.previewImageSrc;
  previewImage.srcset = previewVariant.previewImageSrc;

  if (previewVariant.previewImageAlt != null) {
    previewImage.alt = previewVariant.previewImageAlt;
  }
}

function updatePricePreview(productInfoRoot, previewVariant) {
  if (!(productInfoRoot instanceof HTMLElement) || !previewVariant?.priceCurrent) return;

  const priceContainer = productInfoRoot.querySelector('[data-vo-preview-price]');
  if (!(priceContainer instanceof HTMLElement)) return;

  const current = previewVariant.priceCurrent || '';
  const compare = previewVariant.priceCompare || '';
  const pct = Number(previewVariant.discountPercentage || 0);
  const hasDiscount = !!compare && pct > 0;

  const getTypoClass = (element, fallback) =>
    (element && Array.from(element.classList).find((cls) => cls.startsWith('tw-typo-vo-'))) || fallback;

  // Match the typography of the server-rendered price so the size doesn't jump on hover.
  const existingPrice = priceContainer.querySelector('[data-vo-price]');
  const sizeCurrent =
    existingPrice?.dataset.voPriceSizeCurrent ||
    getTypoClass(priceContainer.querySelector('.vo-price__current'), 'tw-typo-vo-base');
  const sizeCompare =
    existingPrice?.dataset.voPriceSizeCompare ||
    getTypoClass(priceContainer.querySelector('.vo-price__compare'), 'tw-typo-vo-base');
  const sizeBadge =
    existingPrice?.dataset.voPriceSizeBadge ||
    getTypoClass(priceContainer.querySelector('.vo-price__badge span'), 'tw-typo-vo-xs');

  const priceElement = document.createElement('span');
  priceElement.className = 'vo-price tw-inline-flex tw-flex-row tw-items-center tw-gap-sm tw-flex-wrap';
  priceElement.setAttribute('data-vo-price', '');

  // Preserve Discount Kit wiring so cookie/code-based discounts survive the hover preview swap.
  if (existingPrice instanceof HTMLElement) {
    [
      'voDkDiscounts',
      'voDkCurrency',
      'voPriceSizeCurrent',
      'voPriceSizeCompare',
      'voPriceSizeBadge',
      'voPricePrefix',
      'voPriceCurrencySuffix',
    ].forEach((datasetKey) => {
      if (existingPrice.dataset[datasetKey] != null) priceElement.dataset[datasetKey] = existingPrice.dataset[datasetKey];
    });
  }

  const baseCents = Number(previewVariant.priceBaseCents);
  if (Number.isFinite(baseCents) && baseCents > 0) {
    priceElement.dataset.voDkBaseCents = String(baseCents);
  }
  if (previewVariant.manualSale) {
    priceElement.dataset.voDkManualSale = 'true';
  }

  const parts = [];
  if (hasDiscount) {
    parts.push(
      `<s class="vo-price__compare ${sizeCompare} tw-text-vo-neutral tw-line-through tw-no-underline [text-decoration-line:line-through]">${compare}</s>`
    );
  }
  parts.push(
    `<span class="vo-price__current ${sizeCurrent} tw-text-vo-dark !tw-font-medium">${current}</span>`
  );
  if (hasDiscount) {
    parts.push(
      `<span class="vo-price__badge tw-inline-flex tw-items-start tw-gap-xs tw-px-2xs tw-py-3xs tw-rounded-vo-default tw-bg-vo-ultra-dark"><span class="${sizeBadge} tw-text-white tw-font-medium">-${pct}%</span></span>`
    );
  }
  priceElement.innerHTML = parts.join('');

  priceContainer.replaceChildren(priceElement);

  window.VoDkStorefrontPrices?.refreshWithin?.(priceContainer);
}

function updateSizePreview(productInfoRoot, previewVariant) {
  if (!(productInfoRoot instanceof HTMLElement)) return;

  const previewSize = previewVariant?.sizeOption || previewVariant?.size;
  if (previewSize == null) return;

  const sizeValueElement = productInfoRoot.querySelector('[data-vo-preview-size-value]');
  if (sizeValueElement) {
    sizeValueElement.textContent = previewSize;
  }
}

function syncBridgeChoiceDisplay(sectionId, bridgeLabel) {
  const normalizedBridgeLabel = typeof bridgeLabel === 'string' ? bridgeLabel.trim() : '';
  if (!sectionId || !normalizedBridgeLabel) return;

  const productInfoRoot = getProductInfoRoot(sectionId) || document;
  productInfoRoot.querySelectorAll('[data-vo-preview-bridge-value]').forEach((element) => {
    element.textContent = normalizedBridgeLabel;
  });
}

function updateOverlayPreview(productInfoRoot, previewVariant) {
  if (!(productInfoRoot instanceof HTMLElement) || !previewVariant) return;

  const overlaySizeElements = productInfoRoot.querySelectorAll('[data-vo-preview-overlay-size], [data-vo-preview-guide-size]');
  overlaySizeElements.forEach((element) => {
    const nextValue = element.hasAttribute('data-vo-preview-guide-size')
      ? previewVariant.size
      : previewVariant.sizeOption || previewVariant.size;

    if (nextValue != null && nextValue !== '') {
      element.textContent = nextValue;
    }
  });

  const lensWidthElements = productInfoRoot.querySelectorAll('[data-vo-preview-lens-width]');
  lensWidthElements.forEach((element) => {
    if (previewVariant.lensWidth != null && previewVariant.lensWidth !== '') {
      element.textContent = previewVariant.lensWidth;
    }
  });

  const bridgeWidthElements = productInfoRoot.querySelectorAll('[data-vo-preview-bridge-width]');
  bridgeWidthElements.forEach((element) => {
    if (previewVariant.bridgeWidth != null && previewVariant.bridgeWidth !== '') {
      element.textContent = previewVariant.bridgeWidth;
    }
  });

  const measurementKeys = {
    lensWidth: previewVariant.lensWidth,
    bridgeWidth: previewVariant.bridgeWidth,
    templeLength: previewVariant.templeLength,
    lensHeight: previewVariant.lensHeight,
  };

  Object.entries(measurementKeys).forEach(([measurementKey, measurementValue]) => {
    if (measurementValue == null || measurementValue === '') return;

    productInfoRoot.querySelectorAll(`[data-vo-guide-measurement="${measurementKey}"]`).forEach((element) => {
      element.dataset.mmValue = measurementValue;
      const unit = element.closest('vo-measurements-toggler')?.dataset.unit || 'mm';
      element.textContent = formatMeasurementValue(measurementValue, unit);
    });
  });
}

function applyVariantPreview(sectionId, previewVariant) {
  if (!sectionId || !previewVariant) return;

  const productInfoRoot = getProductInfoRoot(sectionId);
  if (!productInfoRoot) return;

  updateGalleryPreview(productInfoRoot, previewVariant);
  updatePricePreview(productInfoRoot, previewVariant);
  updateSizePreview(productInfoRoot, previewVariant);
  updateOverlayPreview(productInfoRoot, previewVariant);
}

function findMatchingVariant(matrix, optionValues) {
  if (!Array.isArray(matrix) || !Array.isArray(optionValues) || !optionValues.length) return null;

  return (
    matrix.find(
      (variant) =>
        Array.isArray(variant.options) &&
        variant.options.length === optionValues.length &&
        variant.options.every((value, index) => value === optionValues[index])
    ) || null
  );
}

function findVariantByPartialOptions(matrix, currentOptionValues, partialOptionValues) {
  if (!Array.isArray(matrix) || !matrix.length) return null;

  const desiredOptionValues = [...currentOptionValues];
  Object.entries(partialOptionValues).forEach(([index, value]) => {
    if (value) desiredOptionValues[Number(index)] = value;
  });

  const exactVariant = findMatchingVariant(matrix, desiredOptionValues);
  if (exactVariant) return exactVariant;

  const matchingVariants = matrix.filter((variant) =>
    Object.entries(partialOptionValues).every(([index, value]) => !value || variant.options?.[Number(index)] === value)
  );

  return matchingVariants.find((variant) => variant.purchasable) || matchingVariants[0] || null;
}

function toNumber(value) {
  if (value == null || value === '') return null;

  const normalizedValue = typeof value === 'number' ? value : Number.parseFloat(String(value).replace(/[^0-9.-]/g, ''));
  return Number.isFinite(normalizedValue) ? normalizedValue : null;
}

function variantInRange(frameWidth, range) {
  const numericWidth = toNumber(frameWidth);
  if (numericWidth === null || !range) return false;

  return numericWidth >= range.min && numericWidth <= range.max;
}

function isVariantPurchasable(variant) {
  return Boolean(variant) && variant.available !== false && variant.purchasable !== false;
}

function getPdpText(scope, datasetKey, fallback = '') {
  const root =
    scope instanceof HTMLElement
      ? scope.closest('[data-vo-overlay-root]') || scope.closest('dialog[data-vo-overlay]')?.closest('[data-vo-overlay-root]')
      : null;

  return root?.dataset?.[datasetKey] || fallback;
}

function getVariantStockMessage(variant, scope = null) {
  if (variant?.availabilityStatus === 'sold-out') return [getPdpText(scope, 'textSoldOut')];

  return [getPdpText(scope, 'textOutOfStock'), getPdpText(scope, 'textCheckAlternatives')].filter(Boolean);
}

function updateVariantStockMessage(stockMessage, variant) {
  if (!(stockMessage instanceof HTMLElement)) return;

  const messageLines = getVariantStockMessage(variant, stockMessage);
  const messageElements = messageLines.map((messageLine) => {
    const line = document.createElement('span');
    line.textContent = messageLine;
    return line;
  });

  stockMessage.replaceChildren(...messageElements);
}

function getRangeMidpoint(range) {
  return (range.min + range.max) / 2;
}

function getSizeOptionIndex(sectionId, dialog = null) {
  const explicitValue = dialog?.querySelector('[data-vo-size-confirm]')?.dataset.sizeOptionIndex;
  const rootValue = getOverlayRoot(sectionId)?.dataset.sizeOptionIndex;
  const numericValue = Number(explicitValue ?? rootValue);

  return Number.isNaN(numericValue) ? null : numericValue;
}

function getSizeChoiceWarning(rangeKey, scope = null) {
  if (rangeKey === 'narrow') return getPdpText(scope, 'textSizeWarningNarrow');
  if (rangeKey === 'wide') return getPdpText(scope, 'textSizeWarningWide');
  return '';
}

function getVariantDisplaySize(variant, sizeOptionIndex) {
  if (!variant) return '';
  return variant.sizeOption || variant.options?.[sizeOptionIndex] || variant.size || '';
}

function normalizeSizeValue(value) {
  return String(value || '')
    .trim()
    .replace(/\s+/g, '')
    .toLowerCase();
}

function variantMatchesSizeTarget(variant, sizeOptionIndex, targetSizeValue) {
  const normalizedTarget = normalizeSizeValue(targetSizeValue);
  if (!variant || !normalizedTarget) return false;

  return [getVariantDisplaySize(variant, sizeOptionIndex), variant.sizeOption, variant.options?.[sizeOptionIndex], variant.size].some(
    (value) => normalizeSizeValue(value) === normalizedTarget
  );
}

function sortSizeValuesAscending(sizeValues) {
  return [...sizeValues].sort((left, right) => {
    const leftNum = toNumber(left);
    const rightNum = toNumber(right);

    if (leftNum !== null && rightNum !== null) return leftNum - rightNum;
    return String(left).localeCompare(String(right), undefined, { numeric: true, sensitivity: 'base' });
  });
}

function buildSizePreferenceOrder(sizeValues, rangeKey) {
  if (!sizeValues.length) return [];

  const sortedSizeValues = sortSizeValuesAscending(sizeValues);

  if (rangeKey === 'narrow') return sortedSizeValues;
  if (rangeKey === 'wide') return [...sortedSizeValues].reverse();

  const middleIndex = Math.floor((sortedSizeValues.length - 1) / 2);
  return [...sortedSizeValues]
    .map((value, index) => ({ value, distance: Math.abs(index - middleIndex) }))
    .sort((left, right) => left.distance - right.distance)
    .map(({ value }) => value);
}

function getVariantFrameWidth(variant) {
  return toNumber(variant?.frameWidth);
}

function isWideFaceSizeRangeKey(rangeKey) {
  return rangeKey === getFaceSizeRange().wide.key;
}

/**
 * Distance metric for ranking variants within a face-size cluster.
 * Mirrors legacy FaceSizePicker `searchVariant` logic.
 * @param {number|null} frameWidth
 * @param {{ min: number, max: number }} range
 * @param {string} rangeKey
 * @returns {number|null}
 */
function getFaceSizeRangeDistance(frameWidth, range, rangeKey) {
  if (frameWidth === null) return null;

  return isWideFaceSizeRangeKey(rangeKey) ? frameWidth - range.min : Math.abs(range.max - frameWidth);
}

function compareFaceSizeRangeDistance(left, right, rangeKey) {
  const faceSizeRange = getFaceSizeRange();
  const leftDistance = left._rangeDistance ?? 0;
  const rightDistance = right._rangeDistance ?? 0;

  if (rangeKey === faceSizeRange.average.key) {
    return leftDistance - rightDistance;
  }

  return rightDistance - leftDistance;
}

function pickExtremeFrameWidthVariant(candidatesWithWidth, rangeKey) {
  const faceSizeRange = getFaceSizeRange();

  if (rangeKey === faceSizeRange.wide.key) {
    return candidatesWithWidth.reduce((widest, variant) =>
      variant._frameWidth > widest._frameWidth ? variant : widest
    );
  }

  return candidatesWithWidth.reduce((narrowest, variant) =>
    variant._frameWidth < narrowest._frameWidth ? variant : narrowest
  );
}

/**
 * Rank size candidates by face-width cluster using settings-driven FACE_SIZE_RANGE.
 * @param {Array<object>} candidates
 * @param {string} rangeKey
 * @param {number} sizeOptionIndex
 * @returns {{ rankedCandidates: Array<object>, isInRange: boolean }}
 */
function getRankedSizeCandidates(candidates, rangeKey, sizeOptionIndex) {
  const faceSizeRange = getFaceSizeRange();
  const selectedRange = faceSizeRange[rangeKey] || faceSizeRange.average;
  const candidatesWithWidth = candidates
    .map((variant) => ({ ...variant, _frameWidth: getVariantFrameWidth(variant) }))
    .filter((variant) => variant._frameWidth !== null);
  const uniqueFrameWidths = [...new Set(candidatesWithWidth.map((variant) => variant._frameWidth))];

  if (candidatesWithWidth.length && uniqueFrameWidths.length > 1) {
    if (rangeKey === faceSizeRange.average.key) {
      const midpoint = getRangeMidpoint(selectedRange);
      const inRangeCandidates = candidatesWithWidth.filter((variant) => variantInRange(variant._frameWidth, selectedRange));
      const rankingPool = inRangeCandidates.length ? inRangeCandidates : candidatesWithWidth;
      const rankedCandidates = [...rankingPool]
        .map((variant) => ({
          ...variant,
          _rangeDistance: Math.abs(variant._frameWidth - midpoint),
        }))
        .sort((left, right) => compareFaceSizeRangeDistance(left, right, rangeKey));

      return { rankedCandidates, isInRange: inRangeCandidates.length > 0 };
    }

    const inRangeCandidates = candidatesWithWidth.filter((variant) => variantInRange(variant._frameWidth, selectedRange));

    if (inRangeCandidates.length) {
      const rankedCandidates = [...inRangeCandidates]
        .map((variant) => ({
          ...variant,
          _rangeDistance: getFaceSizeRangeDistance(variant._frameWidth, selectedRange, rangeKey),
        }))
        .sort((left, right) => compareFaceSizeRangeDistance(left, right, rangeKey));

      return { rankedCandidates, isInRange: true };
    }

    return {
      rankedCandidates: [pickExtremeFrameWidthVariant(candidatesWithWidth, rangeKey)],
      isInRange: false,
    };
  }

  const sizeValues = [
    ...new Set(candidates.map((variant) => variant.options?.[sizeOptionIndex]).filter((value) => value != null && value !== '')),
  ];
  const orderedSizeValues = buildSizePreferenceOrder(sizeValues, rangeKey);
  const rankedCandidates = orderedSizeValues.flatMap((sizeValue) =>
    candidates.filter((variant) => variant.options?.[sizeOptionIndex] === sizeValue)
  );

  return {
    rankedCandidates: rankedCandidates.length ? rankedCandidates : candidates,
    isInRange: true,
  };
}

function getUniqueClusterVariantsByRank(rankedCandidates, range, sizeOptionIndex) {
  const clusterUniqueOrdered = [];
  const seenSizes = new Set();

  rankedCandidates.forEach((variant) => {
    const frameWidth = getVariantFrameWidth(variant);
    if (!variantInRange(frameWidth, range)) return;

    const sizeKey = normalizeSizeValue(getVariantDisplaySize(variant, sizeOptionIndex));
    if (!sizeKey || seenSizes.has(sizeKey)) return;

    seenSizes.add(sizeKey);
    clusterUniqueOrdered.push(variant);
  });

  return clusterUniqueOrdered;
}

/** Best fit for the face cluster + one adjacent size only (max 2 switch targets). */
function getClusterSizePair(rankedCandidates, rangeKey, sizeOptionIndex) {
  const faceSizeRange = getFaceSizeRange();
  const selectedRange = faceSizeRange[rangeKey] || faceSizeRange.average;
  const clusterUniqueOrdered = getUniqueClusterVariantsByRank(rankedCandidates, selectedRange, sizeOptionIndex);
  const primary = clusterUniqueOrdered[0] || rankedCandidates[0] || null;

  if (!primary) {
    return { primary: null, paired: null, clusterCount: 0 };
  }

  return {
    primary,
    paired: clusterUniqueOrdered.length >= 2 ? clusterUniqueOrdered[1] : null,
    clusterCount: clusterUniqueOrdered.length,
  };
}

function getSizeSuggestion(sectionId, rangeKey, preferredVariantId = null, dialog = null, targetSizeValue = '') {
  const matrix = getVariantMatrix(sectionId);
  const sizeOptionIndex = getSizeOptionIndex(sectionId, dialog);
  const currentOptionValues = getCurrentOptionValues(sectionId);

  if (!matrix.length || sizeOptionIndex === null) {
    return null;
  }

  const sizeCandidates = matrix.filter((variant) =>
    variant.options?.every(
      (value, index) => index === sizeOptionIndex || currentOptionValues[index] == null || value === currentOptionValues[index]
    )
  );

  if (!sizeCandidates.length) {
    return null;
  }

  const { rankedCandidates, isInRange } = getRankedSizeCandidates(sizeCandidates, rangeKey, sizeOptionIndex);
  if (!rankedCandidates.length) return null;

  const { primary, paired, clusterCount } = getClusterSizePair(rankedCandidates, rangeKey, sizeOptionIndex);
  if (!primary) return null;

  let suggestedVariant = primary;
  let alternativeVariant = clusterCount >= 2 ? paired : null;

  if (preferredVariantId != null && paired) {
    const preferredVariant = sizeCandidates.find((variant) => String(variant.id) === String(preferredVariantId));
    if (preferredVariant) {
      const preferredId = String(preferredVariant.id);
      const primaryId = String(primary.id);
      const pairedId = String(paired.id);

      if (preferredId === pairedId) {
        suggestedVariant = paired;
        alternativeVariant = primary;
      } else if (preferredId === primaryId) {
        suggestedVariant = primary;
        alternativeVariant = paired;
      }
    }
  }

  const faceSizeRange = getFaceSizeRange();
  const selectedRange = faceSizeRange[rangeKey] || faceSizeRange.average;
  const frameWidth = getVariantFrameWidth(suggestedVariant);
  const inRange = frameWidth === null ? isInRange : variantInRange(frameWidth, selectedRange);
  const hasClusterAlternative =
    clusterCount >= 2 &&
    alternativeVariant &&
    variantInRange(getVariantFrameWidth(alternativeVariant), selectedRange);

  return {
    alternativeVariant: inRange && hasClusterAlternative ? alternativeVariant : null,
    isInRange: inRange,
    rangeKey,
    suggestedVariant,
    warningText: inRange ? '' : getSizeChoiceWarning(rangeKey, dialog),
  };
}

function getAlternativeLabel(alternativeVariant, suggestedVariant, rangeKey, sizeOptionIndex, scope = null) {
  if (!alternativeVariant) return '';

  const alternativeSizeValue = getVariantDisplaySize(alternativeVariant, sizeOptionIndex);
  const alternativeWidth = toNumber(alternativeVariant.frameWidth);
  const suggestedWidth = toNumber(suggestedVariant?.frameWidth);

  let labelPrefix = getPdpText(scope, 'textAlternativeAnotherSize');
  if (alternativeWidth !== null && suggestedWidth !== null) {
    labelPrefix =
      alternativeWidth > suggestedWidth
        ? getPdpText(scope, 'textAlternativeWiderSize')
        : getPdpText(scope, 'textAlternativeNarrowerSize');
  } else if (rangeKey === VO_FACE_SIZE_RANGE.narrow.key) {
    labelPrefix = getPdpText(scope, 'textAlternativeWiderSize');
  } else if (rangeKey === VO_FACE_SIZE_RANGE.wide.key) {
    labelPrefix = getPdpText(scope, 'textAlternativeNarrowerSize');
  }

  return alternativeSizeValue ? `${labelPrefix} (${alternativeSizeValue})` : labelPrefix;
}

function resetSizeDialog(dialog) {
  if (!(dialog instanceof HTMLElement)) return;

  dialog.querySelectorAll('[data-vo-size-choice]').forEach((choiceButton) => {
    choiceButton.classList.remove('is-selected');
    choiceButton.setAttribute('aria-pressed', 'false');
  });

  const sizeConfirmButton = dialog.querySelector('[data-vo-size-confirm]');
  if (sizeConfirmButton) {
    sizeConfirmButton.dataset.voVariantId = '';
    sizeConfirmButton.dataset.voSizeTarget = '';
    sizeConfirmButton.disabled = true;
    sizeConfirmButton.setAttribute('aria-disabled', 'true');
  }

  const feedback = dialog.querySelector('[data-vo-size-feedback]');
  if (feedback) feedback.hidden = true;

  const feedbackLabel = dialog.querySelector('[data-vo-size-feedback-label]');
  if (feedbackLabel) {
    feedbackLabel.textContent = feedbackLabel.dataset.labelDefault || feedbackLabel.textContent;
  }

  const feedbackValue = dialog.querySelector('[data-vo-size-feedback-value]');
  if (feedbackValue) feedbackValue.textContent = '';

  const warning = dialog.querySelector('[data-vo-size-warning]');
  const warningText = dialog.querySelector('[data-vo-size-warning-text]');
  if (warning) warning.hidden = true;
  if (warningText) warningText.textContent = '';

  const alternativeButton = dialog.querySelector('[data-vo-size-alternative]');
  if (alternativeButton) {
    alternativeButton.hidden = true;
    alternativeButton.dataset.voVariantId = '';
    alternativeButton.textContent = '';
  }

  const stockMessage = dialog.querySelector('[data-vo-size-stock]');
  if (stockMessage) stockMessage.hidden = true;
}

function formatMeasurementValue(value, unit = 'mm') {
  const numericValue = toNumber(value);
  if (numericValue === null) return value || '';

  if (unit === 'inch') {
    return `${(numericValue / 25.4).toFixed(2)}in`;
  }

  return `${numericValue}mm`;
}

function updateSizeDialog(dialog, selectedChoice, preferredVariantId = null) {
  if (!(dialog instanceof HTMLElement) || !(selectedChoice instanceof HTMLElement)) return;

  const sizeConfirmButton = dialog.querySelector('[data-vo-size-confirm]');
  const sectionId = sizeConfirmButton?.dataset.sectionId;
  if (!sectionId) return;

  dialog.querySelectorAll('[data-vo-size-choice]').forEach((choiceButton) => {
    const isSelected = choiceButton === selectedChoice;
    choiceButton.classList.toggle('is-selected', isSelected);
    choiceButton.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
  });

  const rangeKey = selectedChoice.dataset.voSizeRange || getFaceSizeRange().average.key;
  const sizeSuggestion = getSizeSuggestion(sectionId, rangeKey, preferredVariantId, dialog, selectedChoice.dataset.voSizeTarget);
  const feedback = dialog.querySelector('[data-vo-size-feedback]');

  if (!sizeSuggestion?.suggestedVariant) {
    if (sizeConfirmButton) {
      sizeConfirmButton.dataset.voVariantId = '';
      sizeConfirmButton.dataset.voSizeTarget = selectedChoice.dataset.voSizeTarget || '';
      sizeConfirmButton.disabled = true;
      sizeConfirmButton.setAttribute('aria-disabled', 'true');
    }
    if (feedback) feedback.hidden = true;
    return;
  }

  const sizeOptionIndex = getSizeOptionIndex(sectionId, dialog);
  const feedbackLabel = dialog.querySelector('[data-vo-size-feedback-label]');
  const feedbackValue = dialog.querySelector('[data-vo-size-feedback-value]');
  const warning = dialog.querySelector('[data-vo-size-warning]');
  const warningText = dialog.querySelector('[data-vo-size-warning-text]');
  const alternativeButton = dialog.querySelector('[data-vo-size-alternative]');
  const stockMessage = dialog.querySelector('[data-vo-size-stock]');

  const suggestedVariant = sizeSuggestion.suggestedVariant;
  const isSuggestedPurchasable = isVariantPurchasable(suggestedVariant);
  const previewSizeValue = getVariantDisplaySize(suggestedVariant, sizeOptionIndex) || selectedChoice.dataset.voSizeTarget;

  if (feedback) feedback.hidden = false;

  if (feedbackLabel) {
    const label = sizeSuggestion.isInRange ? feedbackLabel.dataset.labelInRange : feedbackLabel.dataset.labelDefault;
    feedbackLabel.textContent = label || feedbackLabel.textContent;
  }

  if (feedbackValue) {
    feedbackValue.textContent = previewSizeValue || '';
  }

  if (warning && warningText) {
    warning.hidden = !sizeSuggestion.warningText;
    warningText.textContent = sizeSuggestion.warningText || '';
  }

  if (alternativeButton) {
    if (sizeSuggestion.alternativeVariant) {
      alternativeButton.hidden = false;
      alternativeButton.dataset.voVariantId = String(sizeSuggestion.alternativeVariant.id);
      alternativeButton.textContent = getAlternativeLabel(
        sizeSuggestion.alternativeVariant,
        suggestedVariant,
        rangeKey,
        sizeOptionIndex,
        dialog
      );
    } else {
      alternativeButton.hidden = true;
      alternativeButton.dataset.voVariantId = '';
      alternativeButton.textContent = '';
    }
  }

  if (stockMessage) {
    stockMessage.hidden = isSuggestedPurchasable;
    if (!isSuggestedPurchasable) updateVariantStockMessage(stockMessage, suggestedVariant);
  }

  if (sizeConfirmButton) {
    sizeConfirmButton.dataset.voVariantId = String(suggestedVariant.id);
    sizeConfirmButton.dataset.voSizeTarget = previewSizeValue || '';
    sizeConfirmButton.disabled = false;
    sizeConfirmButton.removeAttribute('aria-disabled');
  }
}

function hydrateSizeDialog(dialog) {
  if (!(dialog instanceof HTMLElement)) return;
  resetSizeDialog(dialog);
}

function normalizeNotifyAppValue(value) {
  return String(value || '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

function getNotifyAppSignatureParts(element) {
  if (!(element instanceof HTMLElement)) return { attributes: '', text: '', all: '' };

  const attributes = normalizeNotifyAppValue(
    Array.from(element.attributes || [])
      .map((attribute) => `${attribute.name} ${attribute.value}`)
      .join(' ')
  );
  const text = normalizeNotifyAppValue(
    [element.textContent, element.getAttribute('aria-label'), element.getAttribute('title'), element.getAttribute('value')]
      .filter(Boolean)
      .join(' ')
      .slice(0, 300)
  );

  return {
    attributes,
    text,
    all: `${attributes} ${text}`.trim(),
  };
}

function notifyAppSignatureHasKeyword(signature) {
  return VO_NOTIFY_APP_KEYWORDS.some((keyword) => signature.includes(keyword));
}

function notifyAppAttributesHaveMarker(attributes) {
  return /(appikon|globo|klaviyo|restock|notify|avvisami|avvisa|notifica|bis|back[-_ ]?in[-_ ]?stock|stock[-_ ]?alert)/.test(
    attributes
  );
}

function isThemeBuyBoxElement(element) {
  return (
    element instanceof HTMLElement &&
    element.matches(
      'product-form, form[data-type="add-to-cart-form"], [data-vo-buy-box], .vo-buy-box__actions, .vo-buy-box__express, .vo-buy-box__payment-button, .vo-buy-box__installments'
    )
  );
}

function isLikelyNotifyAppElement(element) {
  if (!(element instanceof HTMLElement)) return false;
  if (element.closest('[data-vo-notify-app-slot]')) return false;
  if (element.closest(VO_NOTIFY_APP_OWNED_SELECTOR)) return false;
  if (isThemeBuyBoxElement(element)) return false;

  const { attributes, all } = getNotifyAppSignatureParts(element);
  if (!notifyAppSignatureHasKeyword(all)) return false;

  return (
    element.matches('button, a, input, form, iframe, [role="button"]') ||
    notifyAppAttributesHaveMarker(attributes)
  );
}

function getNotifyAppMoveTarget(element, productInfoRoot) {
  if (!(element instanceof HTMLElement)) return null;

  let node = element;
  let target = element;

  while (node && node !== productInfoRoot && node !== document.body) {
    if (
      node.matches?.(
        'product-form, form[action*="/cart/add"], [data-vo-buy-box], [data-vo-overlay], [data-vo-overlay-root]'
      )
    ) {
      break;
    }

    const { attributes } = getNotifyAppSignatureParts(node);
    if (notifyAppAttributesHaveMarker(attributes)) {
      target = node;
    }

    node = node.parentElement;
  }

  return target;
}

function isValidNotifyAppTarget(target) {
  return (
    target instanceof HTMLElement &&
    !target.closest('[data-vo-notify-app-slot]') &&
    !target.closest('[data-vo-overlay]') &&
    !target.matches('script, style, template, noscript')
  );
}

function showNotifyAppTarget(target) {
  if (!(target instanceof HTMLElement)) return;

  target.hidden = false;
  target.removeAttribute('aria-hidden');
  target.style.removeProperty('display');
  target.removeAttribute('data-vo-notify-app-suppressed');
}

function hideNotifyAppTarget(target) {
  if (!(target instanceof HTMLElement)) return;

  target.hidden = true;
  target.setAttribute('aria-hidden', 'true');
  target.dataset.voNotifyAppSuppressed = 'true';
  target.style.setProperty('display', 'none', 'important');
}

function getNotifyAppIdentity(element) {
  if (!(element instanceof HTMLElement)) return '';
  const { attributes, text } = getNotifyAppSignatureParts(element);
  return (element.id || `${attributes} ${text}`).slice(0, 180);
}

function findRestockModalWrapper(node) {
  if (!(node instanceof Element)) return null;
  if (node.matches(VO_NOTIFY_RESTOCK_WRAPPER_SELECTOR)) return node;

  return node.querySelector?.(VO_NOTIFY_RESTOCK_WRAPPER_SELECTOR) || null;
}

function hasRestockContactField(root) {
  return Array.from(root?.querySelectorAll?.(VO_NOTIFY_RESTOCK_CONTACT_FIELD_SELECTOR) || []).some(
    (input) => input instanceof HTMLInputElement && input.type !== 'hidden'
  );
}

function hasNotifyNativeResult(nativeForm) {
  return Boolean(
    nativeForm instanceof HTMLElement &&
      nativeForm.querySelector('[data-vo-overlay-auto-open], .vo-pdp-notify-form__success, .vo-pdp-field--error')
  );
}

function isRestockWrapperSuppressed(wrapper) {
  if (!(wrapper instanceof HTMLElement)) return false;

  const inlineStyle = wrapper.getAttribute('style') || '';
  return (
    !wrapper.isConnected ||
    wrapper.hidden ||
    wrapper.getAttribute('aria-hidden') === 'true' ||
    /display\s*:\s*none/i.test(inlineStyle) ||
    /visibility\s*:\s*hidden/i.test(inlineStyle) ||
    /opacity\s*:\s*0(?:[;\s]|$)/i.test(inlineStyle)
  );
}

function hasRestockSuccessContent(root) {
  if (!(root instanceof HTMLElement)) return false;

  return Array.from(root.querySelectorAll(VO_NOTIFY_RESTOCK_SUCCESS_SELECTOR)).some((element) => {
    if (!(element instanceof HTMLElement) || element.hidden) return false;
    if (element.matches('[data-vo-notify-app-success]')) return true;

    const text = normalizeNotifyAppValue(element.textContent);
    return (
      text.includes('thank') ||
      text.includes('success') ||
      text.includes('subscrib') ||
      text.includes('request') ||
      text.includes('notified')
    );
  });
}

function createNotifySuccessElement(scope = null) {
  const success = document.createElement('div');
  success.className = 'vo-pdp-notify-app__success tw-typo-vo-base tw-text-vo-dark';
  success.setAttribute('data-vo-notify-app-success', '');
  success.setAttribute('role', 'status');
  const message = document.createElement('p');
  message.textContent = getPdpText(scope, 'textNotifySuccess');
  success.replaceChildren(message);
  return success;
}

function storeNotifySubmitState(sectionId) {
  if (!sectionId) return;

  try {
    window.sessionStorage?.setItem(
      VO_NOTIFY_RESTOCK_SUBMIT_STORAGE_KEY,
      JSON.stringify({
        sectionId,
        timestamp: Date.now(),
      })
    );
  } catch (error) {
    // Storage can be unavailable in privacy modes; same-page guards still handle the submit.
  }
}

function consumeNotifySubmitState() {
  let submitState = null;

  try {
    const rawState = window.sessionStorage?.getItem(VO_NOTIFY_RESTOCK_SUBMIT_STORAGE_KEY);
    window.sessionStorage?.removeItem(VO_NOTIFY_RESTOCK_SUBMIT_STORAGE_KEY);
    if (!rawState) return null;

    submitState = JSON.parse(rawState);
  } catch (error) {
    return null;
  }

  if (!submitState?.timestamp || Date.now() - submitState.timestamp > VO_NOTIFY_RESTOCK_SUBMIT_STORAGE_TTL) {
    return null;
  }

  return submitState;
}

function showNotifyPostSubmitSuccess(dialog) {
  if (!(dialog instanceof HTMLDialogElement)) return;

  const appContainer = dialog.querySelector('[data-vo-notify-app-container]');
  const nativeForm = dialog.querySelector('[data-vo-notify-native-form]');
  const nativeSuccess = nativeForm?.querySelector('.vo-pdp-notify-form__success');

  if (nativeSuccess) {
    if (appContainer) appContainer.hidden = true;
    if (nativeForm) nativeForm.hidden = false;
    return;
  }

  if (appContainer) appContainer.hidden = false;
  if (nativeForm) nativeForm.hidden = true;

  const slot = dialog.querySelector('[data-vo-notify-app-slot]');
  if (!(slot instanceof HTMLElement)) return;

  let success = slot.querySelector('[data-vo-notify-app-success]');
  if (!success) success = createNotifySuccessElement(dialog);

  success.hidden = false;
  slot.append(success);
}

function protectNotifyOverlayAutoOpen(dialog, { persist = false } = {}) {
  if (!(dialog instanceof HTMLDialogElement)) return;

  const isPersistent = persist === true;
  dialog.__voAutoReopenUntil = Date.now() + VO_NOTIFY_OVERLAY_AUTO_REOPEN_GUARD;
  dialog.__voAutoReopenPersistent = persist;

  if (!dialog.__voCancelMarksManualClose) {
    dialog.__voCancelMarksManualClose = () => {
      dialog.__voManualCloseAt = Date.now();
    };
    dialog.addEventListener('cancel', dialog.__voCancelMarksManualClose);
  }

  if (dialog.__voAutoReopenHandler) return;

  const cleanup = () => {
    if (dialog.__voAutoReopenHandler) {
      dialog.removeEventListener('close', dialog.__voAutoReopenHandler);
    }
    dialog.__voAutoReopenHandler = null;
    dialog.__voAutoReopenUntil = 0;
    dialog.__voAutoReopenPersistent = false;
  };

  dialog.__voAutoReopenHandler = () => {
    const reopenUntil = Number(dialog.__voAutoReopenUntil || 0);
    const manualCloseAt = Number(dialog.__voManualCloseAt || 0);
    const isPersistent = dialog.__voAutoReopenPersistent === true;

    if (
      (!isPersistent && (!reopenUntil || Date.now() > reopenUntil)) ||
      (manualCloseAt && Date.now() - manualCloseAt < 750)
    ) {
      cleanup();
      return;
    }

    window.requestAnimationFrame(() => {
      if (dialog.open || (!isPersistent && Date.now() > reopenUntil)) return;

      try {
        dialog.showModal();
      } catch (error) {
        return;
      }

      showNotifyPostSubmitSuccess(dialog);
    });
  };

  dialog.addEventListener('close', dialog.__voAutoReopenHandler);
  if (!isPersistent) window.setTimeout(cleanup, VO_NOTIFY_OVERLAY_AUTO_REOPEN_GUARD);
}

function hasNotifyAppReadyContent(root) {
  if (!(root instanceof HTMLElement)) return false;
  if (root.matches('[data-vo-notify-me-app], [data-vo-notify-app-trigger], [data-vo-notify-app-support]')) return false;
  if (root.matches(VO_NOTIFY_RESTOCK_WRAPPER_SELECTOR)) return hasRestockContactField(root);
  if (hasRestockContactField(root)) return true;

  const form = root.matches('form') ? root : root.querySelector('form');
  if (form instanceof HTMLFormElement && hasRestockContactField(form)) return true;

  return Boolean(root.matches('iframe') || root.querySelector('iframe'));
}

function getCurrentNotifyVariantId(sectionId) {
  const productFormInput = sectionId
    ? document.getElementById(`product-form-${sectionId}`)?.querySelector('input[name="id"]')
    : null;
  if (productFormInput?.value) return productFormInput.value;

  const selectedVariantSource = sectionId ? getVariantSelects(sectionId)?.querySelector('[data-selected-variant]') : null;
  if (selectedVariantSource?.textContent) {
    try {
      const selectedVariant = JSON.parse(selectedVariantSource.textContent);
      if (selectedVariant?.id) return String(selectedVariant.id);
    } catch (error) {
      console.error('[Vogue Notify Me] Failed to parse selected variant JSON:', error);
    }
  }

  return new URLSearchParams(window.location.search).get('variant') || '';
}

function syncRestockConfigVariant(variantId) {
  if (!variantId) return;

  window._ReStockConfig = window._ReStockConfig || {};
  window._ReStockConfig.product = window._ReStockConfig.product || {};
  window._ReStockConfig.product.selected_or_first_available_variant_id = variantId;
}

function getNotifyAppTargets(sectionId) {
  const productInfoRoot = getProductInfoRoot(sectionId);
  const searchRoot = document.body;
  if (!searchRoot) return [];

  const targets = [];
  const seen = new Set();

  const addTarget = (target) => {
    if (!isValidNotifyAppTarget(target)) return;

    const identity = getNotifyAppIdentity(target);
    if (seen.has(identity)) return;

    seen.add(identity);
    targets.push(target);
  };

  function addNotifyAppTarget(target) {
    if (!(isLikelyNotifyAppElement(target) || hasNotifyAppReadyContent(target))) return;
    addTarget(target);
  }

  searchRoot.querySelectorAll(VO_NOTIFY_APP_LOCATION_SELECTOR).forEach(addNotifyAppTarget);

  searchRoot.querySelectorAll(VO_NOTIFY_APP_SCAN_SELECTOR).forEach((element) => {
    if (!isLikelyNotifyAppElement(element)) return;

    const target = getNotifyAppMoveTarget(element, productInfoRoot || searchRoot);
    addTarget(target);
  });

  return targets;
}

function hasNotifyAppContent(slot) {
  return Array.from(slot?.children || []).some(
    (child) => {
      if (!(child instanceof HTMLElement)) return false;
      if (child.hidden || child.dataset.voNotifyAppSuppressed === 'true') return false;
      return hasNotifyAppReadyContent(child);
    }
  );
}

function syncNotifyAppSlot(slot) {
  if (!(slot instanceof HTMLElement)) return;

  const overlay = slot.closest('dialog[data-vo-overlay]');
  const sectionId = getSectionIdFromElement(slot);
  if (!(overlay instanceof HTMLDialogElement) || !sectionId) return;

  const existingIdentities = new Set(Array.from(slot.children).map(getNotifyAppIdentity));

  getNotifyAppTargets(sectionId).forEach((target) => {
    const identity = getNotifyAppIdentity(target);
    if (existingIdentities.has(identity)) {
      hideNotifyAppTarget(target);
      return;
    }

    showNotifyAppTarget(target);
    target.dataset.voNotifyAppRelocated = 'true';
    slot.append(target);
    existingIdentities.add(identity);
  });

  const hasAppWidget = hasNotifyAppContent(slot);
  const hasManualNotifyApp = Boolean(slot.querySelector('notify-me-app[data-vo-notify-me-app]'));
  const appContainer = overlay.querySelector('[data-vo-notify-app-container]');
  const nativeForm = overlay.querySelector('[data-vo-notify-native-form]');
  const hasNativeResult = hasNotifyNativeResult(nativeForm);

  if (appContainer) appContainer.hidden = hasNativeResult || !(hasAppWidget || hasManualNotifyApp);
  if (nativeForm) nativeForm.hidden = !hasNativeResult && hasAppWidget;
}

function syncNotifyAppSlots() {
  document.querySelectorAll('[data-vo-notify-app-slot]').forEach(syncNotifyAppSlot);
}

function scheduleNotifyAppIntegration() {
  if (notifyAppIntegrationFrame != null) return;

  notifyAppIntegrationFrame = window.requestAnimationFrame(() => {
    notifyAppIntegrationFrame = null;
    syncNotifyAppSlots();
  });
}

function startNotifyAppObserver() {
  if (notifyAppObserver || !document.documentElement || !('MutationObserver' in window)) return;

  notifyAppObserver = new MutationObserver(scheduleNotifyAppIntegration);
  notifyAppObserver.observe(document.documentElement, {
    childList: true,
    subtree: true,
  });
}

if (!customElements.get('notify-me-app')) {
  customElements.define(
    'notify-me-app',
    class NotifyMeApp extends HTMLElement {
      constructor() {
        super();
        this.button = this.querySelector('.custom-restock-button');
        this.productContent = this.querySelector('.notify-me-content--product-info');
        this.bottomContent = this.querySelector('.notify-me-content--bottom');
        this.closeSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="9.192" height="9.192" viewBox="0 0 9.192 9.192"><g transform="translate(4.596 -3.889) rotate(45)"><line y2="12" transform="translate(6)" fill="none" stroke="#1a1818" stroke-width="1"/><line y2="12" transform="translate(12 6) rotate(90)" fill="none" stroke="#1a1818" stroke-width="1"/></g></svg>`;
        this.currentSizeElement = document.querySelector('[data-sizecontent]');
        this.restockWrapper = null;
        this.lastVariantId = null;
        this.activationTimer = null;
        this.activationAttempts = 0;
        this.pendingWrapperObserver = null;
        this.pendingWrapperTimer = null;
        this.restockSubmitObserver = null;
        this.restockSubmissionActive = false;
        this.restoreAfterSubmitTimer = null;
        this.observedOverlay = null;

        this.handleButtonClick = this.handleButtonClick.bind(this);
        this.onVariantChange = this.onVariantChange.bind(this);
        this.handleRestockSubmitStart = this.handleRestockSubmitStart.bind(this);
        this.handleRestockOverlayClose = this.handleRestockOverlayClose.bind(this);

        if (this.button) {
          this.button.addEventListener('click', this.handleButtonClick);
        }
      }

      connectedCallback() {
        if (!this.unsubVariantChange && typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubVariantChange = subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange);
        }

        const selected =
          getVariantSelects(this.sectionId)?.querySelector('[data-selected-variant]') ||
          document.querySelector('variant-selects [data-selected-variant]');
        if (selected) {
          try {
            const variant = JSON.parse(selected.innerHTML);
            this.setVariantId(variant?.id);
          } catch (error) {
            console.error('[NotifyMeApp] Failed to parse selected variant JSON:', error, { raw: selected.innerHTML });
          }
        }
      }

      disconnectedCallback() {
        this.restockWrapper?.removeEventListener('click', this.handleRestockSubmitStart, true);
        this.restockWrapper?.removeEventListener('submit', this.handleRestockSubmitStart, true);
        this.modalObserver?.disconnect();
        this.pendingWrapperObserver?.disconnect();
        this.unsubVariantChange?.();
        this.stopRestockSubmitLifecycle();
        window.clearTimeout(this.activationTimer);
        window.clearTimeout(this.pendingWrapperTimer);
      }

      get sectionId() {
        return this.dataset.sectionId || getSectionIdFromElement(this);
      }

      get slot() {
        return this.closest('[data-vo-notify-app-slot]');
      }

      get overlay() {
        return this.closest('dialog[data-vo-overlay]');
      }

      setVariantId(id) {
        if (!id) return;

        this.lastVariantId = id;
        this.button?.setAttribute('data-variant-id', String(id));
        this.dataset.variantId = String(id);
        syncRestockConfigVariant(id);
        this.syncModalVariant();
      }

      onVariantChange({ data }) {
        if (data?.sectionId && data.sectionId !== this.sectionId) return;
        this.setVariantId(data?.variant?.id);
      }

      activate() {
        this.setVariantId(getCurrentNotifyVariantId(this.sectionId));
        this.overlay?.querySelector('[data-vo-notify-app-container]')?.removeAttribute('hidden');

        const existingWrapper = this.findExistingWrapper();
        if (existingWrapper) {
          this.waitForRestockContent(existingWrapper);
          return;
        }

        this.activationAttempts = 0;
        this.clickRestockTrigger();
      }

      clickRestockTrigger() {
        if (this.restockWrapper || this.findExistingWrapper()) {
          const wrapper = this.findExistingWrapper();
          if (wrapper) this.waitForRestockContent(wrapper);
          return;
        }

        this.activationAttempts += 1;
        this.button?.click();

        if (this.activationAttempts >= 4) return;

        window.clearTimeout(this.activationTimer);
        this.activationTimer = window.setTimeout(() => {
          this.clickRestockTrigger();
        }, this.activationAttempts === 1 ? 150 : 350);
      }

      handleButtonClick() {
        this.setVariantId(getCurrentNotifyVariantId(this.sectionId));

        const observer = new MutationObserver((mutations) => {
          for (const { addedNodes } of mutations) {
            for (const node of addedNodes) {
              if (node.nodeType === 1) {
                if (node.classList?.contains('restock-alerts-modal-wrapper')) {
                  this.restockWrapper = node;
                } else {
                  this.restockWrapper = node.querySelector?.('.restock-alerts-modal-wrapper') || this.restockWrapper;
                }

                if (this.restockWrapper) {
                  observer.disconnect();
                  this.waitForRestockContent(this.restockWrapper);
                  this.syncModalVariant();
                  break;
                }
              }
            }

            if (this.restockWrapper) break;
          }
        });

        observer.observe(document.body, { childList: true, subtree: true });
      }

      findExistingWrapper() {
        return (
          this.slot?.querySelector(VO_NOTIFY_RESTOCK_WRAPPER_SELECTOR) ||
          document.querySelector(`${VO_NOTIFY_RESTOCK_WRAPPER_SELECTOR}:not([data-vo-notify-app-mounted])`)
        );
      }

      setPendingRestockWrapper(wrapper, isPending) {
        if (!(wrapper instanceof HTMLElement)) return;

        if (isPending) {
          wrapper.dataset.voNotifyAppPending = 'true';
          wrapper.style.setProperty('display', 'none', 'important');
          wrapper.style.setProperty('pointer-events', 'none', 'important');
          return;
        }

        delete wrapper.dataset.voNotifyAppPending;
        wrapper.style.removeProperty('display');
        wrapper.style.removeProperty('pointer-events');
      }

      waitForRestockContent(wrapper) {
        if (!(wrapper instanceof HTMLElement)) return;

        this.restockWrapper = wrapper;
        if (hasRestockContactField(wrapper)) {
          this.setPendingRestockWrapper(wrapper, false);
          this.customizePopup(wrapper);
          return;
        }

        this.setPendingRestockWrapper(wrapper, true);
        this.pendingWrapperObserver?.disconnect();
        window.clearTimeout(this.pendingWrapperTimer);

        const finishWhenReady = () => {
          if (!hasRestockContactField(wrapper)) return false;

          this.pendingWrapperObserver?.disconnect();
          this.pendingWrapperObserver = null;
          window.clearTimeout(this.pendingWrapperTimer);
          this.setPendingRestockWrapper(wrapper, false);
          this.customizePopup(wrapper);
          return true;
        };

        this.pendingWrapperObserver = new MutationObserver(finishWhenReady);
        this.pendingWrapperObserver.observe(wrapper, {
          attributes: true,
          childList: true,
          subtree: true,
        });

        this.pendingWrapperTimer = window.setTimeout(() => {
          if (finishWhenReady()) return;

          this.pendingWrapperObserver?.disconnect();
          this.pendingWrapperObserver = null;
          this.setPendingRestockWrapper(wrapper, false);
          wrapper.hidden = true;

          const nativeForm = this.overlay?.querySelector('[data-vo-notify-native-form]');
          if (nativeForm) nativeForm.hidden = false;
        }, 2500);
      }

      syncModalVariant() {
        if (!this.restockWrapper || !this.lastVariantId) return;

        const inputs = this.restockWrapper.querySelectorAll(
          'input[name="variant"], input[name="variant_id"], input[data-variant-input]'
        );
        inputs.forEach((input) => {
          input.value = String(this.lastVariantId);
        });

        this.restockWrapper.setAttribute('data-variant-id', String(this.lastVariantId));
      }

      hideRestockTriggers() {
        this.slot
          ?.querySelectorAll(
            '[data-vo-notify-app-block] .custom-restock-button, [data-vo-notify-app-block] .restock-alerts-notify-button, [data-vo-notify-app-relocated="true"].custom-restock-button, [data-vo-notify-app-relocated="true"] .custom-restock-button, [data-vo-notify-app-relocated="true"].restock-alerts-notify-button, [data-vo-notify-app-relocated="true"] .restock-alerts-notify-button'
          )
          .forEach(hideNotifyAppTarget);
      }

      isRestockSubmitEvent(event) {
        if (!(this.restockWrapper instanceof HTMLElement)) return false;

        if (event.type === 'submit') {
          return event.target instanceof HTMLFormElement && this.restockWrapper.contains(event.target);
        }

        const submitControl =
          event.target instanceof Element ? event.target.closest(VO_NOTIFY_RESTOCK_SUBMIT_SELECTOR) : null;
        return Boolean(submitControl && this.restockWrapper.contains(submitControl));
      }

      handleRestockSubmitStart(event) {
        if (!this.isRestockSubmitEvent(event)) return;

        storeNotifySubmitState(this.sectionId);
        this.restockSubmissionActive = true;
        this.watchRestockSubmitLifecycle(this.restockWrapper);
      }

      watchRestockSubmitLifecycle(wrapper) {
        if (!(wrapper instanceof HTMLElement)) return;

        const overlay = this.overlay;
        if (!(overlay instanceof HTMLDialogElement)) return;

        wrapper.removeEventListener('click', this.handleRestockSubmitStart, true);
        wrapper.removeEventListener('submit', this.handleRestockSubmitStart, true);
        wrapper.addEventListener('click', this.handleRestockSubmitStart, true);
        wrapper.addEventListener('submit', this.handleRestockSubmitStart, true);

        if (!this.restockSubmissionActive) return;

        this.restockSubmitObserver?.disconnect();
        this.observedOverlay?.removeEventListener('close', this.handleRestockOverlayClose);
        this.observedOverlay = overlay;
        overlay.dataset.voNotifySubmitActive = 'true';
        overlay.addEventListener('close', this.handleRestockOverlayClose);

        const scheduleRestore = () => {
          window.clearTimeout(this.restoreAfterSubmitTimer);
          this.restoreAfterSubmitTimer = window.setTimeout(
            () => this.restoreRestockOverlayAfterSubmit(),
            VO_NOTIFY_RESTOCK_SUBMIT_RESTORE_DELAY
          );
        };

        this.restockSubmitObserver = new MutationObserver(scheduleRestore);
        this.restockSubmitObserver.observe(wrapper, {
          attributes: true,
          attributeFilter: ['style', 'hidden', 'aria-hidden', 'class'],
          childList: true,
          subtree: true,
        });

        if (this.slot) {
          this.restockSubmitObserver.observe(this.slot, { childList: true });
        }

        scheduleRestore();
      }

      stopRestockSubmitLifecycle() {
        this.restockSubmissionActive = false;
        this.restockSubmitObserver?.disconnect();
        this.restockSubmitObserver = null;
        this.observedOverlay?.removeEventListener('close', this.handleRestockOverlayClose);
        this.observedOverlay?.removeAttribute('data-vo-notify-submit-active');
        this.observedOverlay = null;
        window.clearTimeout(this.restoreAfterSubmitTimer);
      }

      showRestockSuccessFallback(wrapper) {
        if (!(wrapper instanceof HTMLElement) || hasRestockSuccessContent(wrapper)) return;

        let success = wrapper.querySelector('[data-vo-notify-app-success]');
        if (!success) success = createNotifySuccessElement();

        success.hidden = false;
        wrapper.dataset.voNotifySubmitted = 'true';

        const restockForm = wrapper.querySelector('.restock-alerts-form');
        if (restockForm instanceof HTMLElement) restockForm.hidden = true;

        const successMount =
          wrapper.querySelector('.modalBoxLowerSectionStyle') ||
          wrapper.querySelector('.restock-alerts-modal-box') ||
          wrapper;
        successMount.append(success);
      }

      restoreRestockOverlayAfterSubmit() {
        if (!this.restockSubmissionActive) return;

        const wrapper = this.restockWrapper;
        const overlay = this.overlay || this.observedOverlay;
        const slot = this.slot || overlay?.querySelector('[data-vo-notify-app-slot]');
        if (!(wrapper instanceof HTMLElement) || !(overlay instanceof HTMLDialogElement)) return;

        const wasSuppressed = isRestockWrapperSuppressed(wrapper);
        const hadSuccessContent = hasRestockSuccessContent(wrapper);
        const lostContactField = !hasRestockContactField(wrapper);

        if (slot instanceof HTMLElement && wrapper.parentElement !== slot) {
          slot.append(wrapper);
        }

        delete wrapper.dataset.voNotifyAppPending;
        wrapper.hidden = false;
        wrapper.removeAttribute('hidden');
        wrapper.removeAttribute('aria-hidden');
        wrapper.style.removeProperty('display');
        wrapper.style.removeProperty('visibility');
        wrapper.style.removeProperty('opacity');
        wrapper.style.removeProperty('pointer-events');

        const appContainer = overlay.querySelector('[data-vo-notify-app-container]');
        const nativeForm = overlay.querySelector('[data-vo-notify-native-form]');
        if (appContainer) appContainer.hidden = false;
        if (nativeForm) nativeForm.hidden = true;

        if (!overlay.open && typeof overlay.showModal === 'function') {
          try {
            overlay.showModal();
          } catch (error) {
            return;
          }
        }

        if (wasSuppressed || hadSuccessContent || lostContactField) {
          this.showRestockSuccessFallback(wrapper);
        }
      }

      handleRestockOverlayClose() {
        if (!this.restockSubmissionActive) return;

        const overlay = this.observedOverlay;
        if (!(overlay instanceof HTMLDialogElement)) return;

        const manualCloseAt = Number(overlay.__voManualCloseAt || 0);
        if (manualCloseAt && Date.now() - manualCloseAt < 750) {
          this.stopRestockSubmitLifecycle();
          return;
        }

        window.requestAnimationFrame(() => {
          if (!this.restockSubmissionActive || overlay.open) return;

          try {
            overlay.showModal();
          } catch (error) {
            return;
          }

          this.restoreRestockOverlayAfterSubmit();
        });
      }

      customizePopup(wrapper) {
        const restockClose = wrapper.querySelector('.restock-alerts-modal-close-btn');
        const restockModalBox = wrapper.querySelector('.restock-alerts-modal-box');
        const restockForm = wrapper.querySelector('.restock-alerts-form');
        const restockFormTitle = wrapper.querySelector('.restock-alerts-form-title');
        const restockLowerSection = wrapper.querySelector('.modalBoxLowerSectionStyle');
        const restockSubmitButton = wrapper.querySelector('.restock-alerts-submit-button');

        if (restockModalBox) {
          restockModalBox.classList.add('restock-popup-open');
        }

        if (this.closeSvg && restockClose) {
          const encoded = `data:image/svg+xml,${encodeURIComponent(this.closeSvg)}`;
          restockClose.setAttribute('src', encoded);
        }

        if (this.productContent && restockForm && restockFormTitle) {
          restockForm.insertBefore(this.productContent, restockFormTitle.nextSibling);
          this.productContent.classList.remove('tw-hidden');
        }

        if (this.bottomContent && restockLowerSection && restockSubmitButton) {
          restockLowerSection.insertBefore(this.bottomContent, restockSubmitButton.nextSibling);
          this.bottomContent.classList.remove('tw-hidden');
        }

        const sizeValue = wrapper.querySelector('.size-js');
        if (
          this.currentSizeElement &&
          sizeValue &&
          sizeValue.innerHTML.trim() !== this.currentSizeElement.innerHTML.trim()
        ) {
          sizeValue.innerHTML = this.currentSizeElement.innerHTML.trim();
        }

        this.mountRestockWrapper(wrapper);
      }

      mountRestockWrapper(wrapper) {
        if (!(wrapper instanceof HTMLElement)) return;

        this.restockWrapper = wrapper;
        wrapper.dataset.voNotifyAppMounted = 'true';
        wrapper.dataset.voNotifyAppRelocated = 'true';

        this.slot?.append(wrapper);
        showNotifyAppTarget(wrapper);
        this.hideRestockTriggers();

        const appContainer = this.overlay?.querySelector('[data-vo-notify-app-container]');
        const nativeForm = this.overlay?.querySelector('[data-vo-notify-native-form]');
        if (appContainer) appContainer.hidden = false;
        if (nativeForm) nativeForm.hidden = hasRestockContactField(wrapper);

        this.syncModalVariant();
        this.watchRestockSubmitLifecycle(wrapper);
        scheduleNotifyAppIntegration();
      }
    }
  );
}

function activateNotifyAppDialog(dialog) {
  if (!(dialog instanceof HTMLDialogElement)) return;

  const app = dialog.querySelector('notify-me-app[data-vo-notify-me-app]');
  if (!app) return;

  if (typeof app.activate === 'function') {
    app.activate();
    return;
  }

  app.querySelector('[data-vo-notify-app-trigger]')?.click();
}

function getNotifyOverlayForElement(element) {
  const sectionId =
    getSectionIdFromElement(element) ||
    element?.closest('product-info')?.dataset.section ||
    document.querySelector('product-info[data-section]')?.dataset.section;

  return sectionId ? document.getElementById(`VoNotifyOverlay-${sectionId}`) : null;
}

function removeExpressCheckoutForNotifyState(notifyOverlay) {
  if (!(notifyOverlay instanceof HTMLDialogElement)) return;

  const sectionId = getSectionIdFromElement(notifyOverlay) || notifyOverlay.id?.replace(/^VoNotifyOverlay-/, '');
  if (!sectionId) return;

  document
    .querySelectorAll(
      `[data-vo-buy-box][data-section-id="${sectionId}"] [data-vo-express], product-info[data-section="${sectionId}"] [data-vo-express]`
    )
    .forEach((expressCheckout) => expressCheckout.remove());
}

function findExternalNotifyAppElement(target) {
  if (!(target instanceof HTMLElement)) return null;

  let element = target;
  while (element && element !== document.body) {
    if (element.closest?.('[data-vo-overlay]')) return null;
    if (isLikelyNotifyAppElement(element)) return element;

    element = element.parentElement;
  }

  return null;
}

function handleExternalNotifyAppClick(event) {
  const target = findExternalNotifyAppElement(event.target);
  if (!target) return;

  const notifyOverlay = getNotifyOverlayForElement(target);
  if (!(notifyOverlay instanceof HTMLDialogElement)) return;

  event.preventDefault();
  event.stopImmediatePropagation();
  scheduleNotifyAppIntegration();
  removeExpressCheckoutForNotifyState(notifyOverlay);
  openOverlayDialog(notifyOverlay, target);
}

function getNotifyEmailErrorMessage(input) {
  return input?.dataset.voNotifyErrorMessage || getPdpText(input, 'textNotifyEmailError');
}

function getNotifyEmailParts(form) {
  if (!(form instanceof HTMLFormElement) || !form.matches('.vo-pdp-notify-form')) return null;

  const input = form.querySelector('[data-vo-notify-email]');
  const error = form.querySelector('[data-vo-notify-email-error]');
  if (!input || !error) return null;

  return { input, error };
}

function clearNotifyEmailError(input, error) {
  if (!input || !error) return;

  error.textContent = getNotifyEmailErrorMessage(input);
  error.hidden = true;
  input.removeAttribute('aria-invalid');
  input.removeAttribute('aria-describedby');
  input.closest('.vo-pdp-field')?.classList.remove('vo-pdp-field--error');
}

function showNotifyEmailError(input, error, message) {
  if (!input || !error) return;

  error.textContent = message;
  error.hidden = false;
  input.setAttribute('aria-invalid', 'true');
  input.closest('.vo-pdp-field')?.classList.add('vo-pdp-field--error');

  if (error.id) {
    input.setAttribute('aria-describedby', error.id);
  }
}

function isNotifyEmailValid(input) {
  if (!input) return true;

  return input.value.trim() !== '' && input.validity.valid;
}

function validateNotifyEmailForm(form, { focus = false } = {}) {
  const parts = getNotifyEmailParts(form);
  if (!parts) return;

  const { input, error } = parts;
  if (isNotifyEmailValid(input)) {
    clearNotifyEmailError(input, error);
    return true;
  }

  showNotifyEmailError(input, error, getNotifyEmailErrorMessage(input));
  if (focus) input.focus();
  return false;
}

function handleNotifyEmailClick(event) {
  const submitButton = event.target instanceof Element ? event.target.closest('[data-vo-notify-submit]') : null;
  const form = submitButton?.closest('.vo-pdp-notify-form');
  if (!submitButton || !form) return;

  event.preventDefault();
  if (validateNotifyEmailForm(form, { focus: true }) === false) {
    event.stopImmediatePropagation();
    return;
  }

  if (typeof form.requestSubmit === 'function') {
    form.requestSubmit();
  } else {
    form.submit();
  }
}

function handleNotifyEmailSubmit(event) {
  if (validateNotifyEmailForm(event.target, { focus: true }) !== false) return;

  event.preventDefault();
  event.stopImmediatePropagation();
}

function handleNotifyEmailInput(event) {
  const input = event.target;
  if (!(input instanceof HTMLInputElement) || !input.matches('[data-vo-notify-email]')) return;

  const form = input.closest('.vo-pdp-notify-form');
  const parts = getNotifyEmailParts(form);
  if (parts && !parts.error.hidden) clearNotifyEmailError(parts.input, parts.error);
}

function openOverlayDialog(dialog, opener = null) {
  if (!(dialog instanceof HTMLDialogElement)) return;
  if (dialog.open) return;

  dialog.__voOpenedBy = opener || null;
  delete dialog.__voManualCloseAt;
  if (dialog.querySelector('[data-vo-notify-app-slot]')) {
    scheduleNotifyAppIntegration();
  }
  dialog.showModal();
  if (opener && dialog.querySelector('[data-vo-notify-app-slot]')) {
    activateNotifyAppDialog(dialog);
  }

  if (dialog.querySelector('[data-vo-size-choice]')) {
    hydrateSizeDialog(dialog);
  }

  dialog.querySelectorAll('vo-measurements-toggler').forEach((toggle) => {
    if (typeof toggle.setMeasureUnit === 'function') {
      toggle.setMeasureUnit(toggle.dataset.defaultUnit || 'mm', { dispatchEvent: false });
    }
  });

  const focusTarget =
    dialog.querySelector('[data-vo-overlay-initial-focus]') ||
    dialog.querySelector(
      'button:not([data-vo-notify-app-trigger]):not([tabindex="-1"]), [href], input:not([type="hidden"]), select, textarea'
    );
  if (focusTarget && typeof focusTarget.focus === 'function') focusTarget.focus();
}

function closeOverlayDialog(dialog, shouldRestoreFocus = true) {
  if (!(dialog instanceof HTMLDialogElement) || !dialog.open) return;

  const openedBy = dialog.__voOpenedBy;
  dialog.__voManualCloseAt = Date.now();
  dialog.close();

  if (shouldRestoreFocus && openedBy && typeof openedBy.focus === 'function') openedBy.focus();
}

function switchOverlayDialog(currentDialog, nextSelector, opener = null) {
  if (!(currentDialog instanceof HTMLDialogElement)) return;

  const nextDialog = document.querySelector(nextSelector);
  closeOverlayDialog(currentDialog, false);

  if (nextDialog) openOverlayDialog(nextDialog, opener || currentDialog.__voOpenedBy || null);
}

function closeAlertMessage(closeButton) {
  const alert = closeButton.closest('[data-vo-alert]') || closeButton.closest('.tw-vo-alert');
  if (!alert) return;

  alert.hidden = true;
  alert.setAttribute('hidden', '');
  alert.style.display = 'none';
}

document.addEventListener('click', (event) => {
  const overlayTrigger = event.target.closest('[data-vo-overlay-trigger]');
  if (overlayTrigger) {
    event.preventDefault();
    const targetDialog = document.querySelector(overlayTrigger.dataset.voOverlayTarget);
    if (targetDialog) openOverlayDialog(targetDialog, overlayTrigger);
    return;
  }

  const overlaySwitch = event.target.closest('[data-vo-overlay-switch]');
  if (overlaySwitch) {
    event.preventDefault();
    const currentDialog = overlaySwitch.closest('dialog[data-vo-overlay]');
    if (currentDialog) switchOverlayDialog(currentDialog, overlaySwitch.dataset.voOverlaySwitch, overlaySwitch);
    return;
  }

  const overlayClose = event.target.closest('[data-vo-overlay-close]');
  if (overlayClose) {
    event.preventDefault();
    closeOverlayDialog(overlayClose.closest('dialog[data-vo-overlay]'));
    return;
  }

  const colorSelectButton = event.target.closest('[data-vo-color-select]');
  if (colorSelectButton) {
    event.preventDefault();
    const partialOptionValues = getPartialOptionValuesFromDataset(colorSelectButton.dataset);

    const productUrl = colorSelectButton.dataset.voProductUrl;
    if (productUrl) {
      if (!colorSelectButton.classList.contains('is-selected')) {
        navigateToLinkedProduct(getSectionIdFromElement(colorSelectButton), productUrl, partialOptionValues);
      }
      return;
    }

    const sectionId =
      colorSelectButton.dataset.sectionId || colorSelectButton.closest('[data-vo-overlay-root]')?.dataset.sectionId;
    if (!sectionId) return;

    const matrix = getVariantMatrix(sectionId);
    const currentOptionValues = getCurrentOptionValues(sectionId);
    const nextVariant = findVariantByPartialOptions(matrix, currentOptionValues, partialOptionValues);

    if (nextVariant) {
      applyVariantOptionValues(sectionId, nextVariant.options);
    }

    closeOverlayDialog(colorSelectButton.closest('dialog[data-vo-overlay]'), false);
    return;
  }

  const sizeAlternative = event.target.closest('[data-vo-size-alternative]');
  if (sizeAlternative) {
    event.preventDefault();

    const dialog = sizeAlternative.closest('dialog[data-vo-overlay]');
    const selectedChoice = dialog?.querySelector('[data-vo-size-choice].is-selected');
    if (dialog && selectedChoice && sizeAlternative.dataset.voVariantId) {
      updateSizeDialog(dialog, selectedChoice, sizeAlternative.dataset.voVariantId);
    }
    return;
  }

  const sizeChoice = event.target.closest('[data-vo-size-choice]');
  if (sizeChoice) {
    event.preventDefault();
    updateSizeDialog(sizeChoice.closest('dialog[data-vo-overlay]'), sizeChoice);
    return;
  }

  const sizeConfirm = event.target.closest('[data-vo-size-confirm]');
  if (sizeConfirm) {
    event.preventDefault();

    const dialog = sizeConfirm.closest('dialog[data-vo-overlay]');
    const sectionId = sizeConfirm.dataset.sectionId;
    const variantId = sizeConfirm.dataset.voVariantId;
    const matrix = getVariantMatrix(sectionId);
    const suggestedVariant = matrix.find((variant) => String(variant.id) === String(variantId));

    if (suggestedVariant) {
      const nextUrl = new URL(window.location.href);
      nextUrl.searchParams.set('variant', suggestedVariant.id);
      window.history.replaceState({}, '', `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`);
      applyVariantOptionValues(sectionId, suggestedVariant.options);
    } else if (sizeConfirm.dataset.voSizeTarget) {
      applySizeChoice(sectionId, sizeConfirm.dataset.voSizeTarget, sizeConfirm.dataset.sizeOptionIndex);
    }

    closeOverlayDialog(dialog);
    return;
  }

  const bridgeChoice = event.target.closest('[data-vo-bridge-choice]');
  if (bridgeChoice) {
    event.preventDefault();

    const dialog = bridgeChoice.closest('dialog[data-vo-overlay]');
    if (!dialog) return;

    dialog.querySelectorAll('[data-vo-bridge-choice]').forEach((choiceButton) => {
      const isSelected = choiceButton === bridgeChoice;
      choiceButton.classList.toggle('is-selected', isSelected);
      choiceButton.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
    });
    syncBridgeChoiceDisplay(getSectionIdFromElement(bridgeChoice), bridgeChoice.dataset.voBridgeLabel);
    return;
  }

  const bridgeConfirm = event.target.closest('[data-vo-bridge-confirm]');
  if (bridgeConfirm) {
    event.preventDefault();

    const dialog = bridgeConfirm.closest('dialog[data-vo-overlay]');
    const selectedChoice =
      dialog?.querySelector('[data-vo-bridge-choice].is-selected') || dialog?.querySelector('[data-vo-bridge-choice]');
    const bridgeUrl = selectedChoice?.dataset.voBridgeUrl;

    syncBridgeChoiceDisplay(getSectionIdFromElement(bridgeConfirm), selectedChoice?.dataset.voBridgeLabel);

    if (bridgeUrl && selectedChoice?.dataset.voBridgeCurrent !== 'true') {
      navigateToLinkedProduct(getSectionIdFromElement(bridgeConfirm), bridgeUrl);
      return;
    }

    closeOverlayDialog(dialog);
    return;
  }

  if (event.target instanceof HTMLDialogElement && event.target.matches('[data-vo-overlay]')) {
    closeOverlayDialog(event.target, false);
    return;
  }

  const ctaButton = event.target.closest('[data-vo-cta-button]');
  if (ctaButton) {
    const ctaState = ctaButton.dataset.voCtaState;

    if (ctaState === VO_CTA_STATES.notifyMe) {
      event.preventDefault();
      const notifyModal = document.querySelector(ctaButton.dataset.voNotifyModal);
      if (notifyModal) {
        scheduleNotifyAppIntegration();
        removeExpressCheckoutForNotifyState(notifyModal);
        openOverlayDialog(notifyModal, ctaButton);
      }
      return;
    }

    if (ctaState === VO_CTA_STATES.viewAlternatives) {
      event.preventDefault();
      const alternativesUrl = ctaButton.dataset.voAlternativesUrl;
      if (alternativesUrl) window.location.assign(alternativesUrl);
      return;
    }
  }

  const closeButton = event.target.closest(
    '[data-vo-alert-close], .tw-vo-alert button[title="Close"], .tw-vo-alert button[aria-label="Dismiss message"]'
  );
  if (!closeButton) return;

  event.preventDefault();
  closeAlertMessage(closeButton);
});

document.addEventListener('submit', handleNotifyEmailSubmit, true);
document.addEventListener('click', handleNotifyEmailClick, true);
document.addEventListener('click', handleExternalNotifyAppClick, true);
document.addEventListener('input', handleNotifyEmailInput);

startNotifyAppObserver();
scheduleNotifyAppIntegration();

window.addEventListener('load', () => {
  scheduleNotifyAppIntegration();
  const notifySubmitState = consumeNotifySubmitState();
  let handledNotifySubmitState = false;

  document.querySelectorAll('dialog[data-vo-overlay]').forEach((dialog) => {
    const isNotifySubmitDialog =
      notifySubmitState?.sectionId && dialog.id === `VoNotifyOverlay-${notifySubmitState.sectionId}`;
    const shouldAutoOpen = dialog.querySelector('[data-vo-overlay-auto-open]') || isNotifySubmitDialog;

    if (!shouldAutoOpen) return;

    if (isNotifySubmitDialog) {
      showNotifyPostSubmitSuccess(dialog);
      handledNotifySubmitState = true;
    }

    if (dialog.id?.startsWith('VoNotifyOverlay-')) {
      removeExpressCheckoutForNotifyState(dialog);
      protectNotifyOverlayAutoOpen(dialog, { persist: true });
    }

    openOverlayDialog(dialog);
  });

  if (notifySubmitState && !handledNotifySubmitState) {
    const dialog =
      document.getElementById(`VoNotifyOverlay-${notifySubmitState.sectionId}`) ||
      document.querySelector('dialog[id^="VoNotifyOverlay-"]');
    if (dialog instanceof HTMLDialogElement) {
      showNotifyPostSubmitSuccess(dialog);
      removeExpressCheckoutForNotifyState(dialog);
      protectNotifyOverlayAutoOpen(dialog, { persist: true });
      openOverlayDialog(dialog);
    }
  }

  const params = new URLSearchParams(window.location.search);
  if (params.get('notify') === '1') {
    const notifyOverlay = document.querySelector('dialog[id^="VoNotifyOverlay-"]');
    if (notifyOverlay instanceof HTMLDialogElement) {
      scheduleNotifyAppIntegration();
      removeExpressCheckoutForNotifyState(notifyOverlay);
      protectNotifyOverlayAutoOpen(notifyOverlay);
      activateNotifyAppDialog(notifyOverlay);
      openOverlayDialog(notifyOverlay);
    }
    params.delete('notify');
    const cleanQuery = params.toString();
    const cleanUrl = `${window.location.pathname}${cleanQuery ? `?${cleanQuery}` : ''}${window.location.hash}`;
    window.history.replaceState({}, '', cleanUrl);
  }
});
